import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Calendar, Users, CreditCard, TrendingUp, Eye, Trash2, Tag,
  Check, X, Send, RefreshCw, Phone, Mail, Key, MapPin, Clock, Shield,
  DollarSign, Save, Edit3, Layout, ImagePlus, RotateCcw, FileText, Download, Unlock, Lock, Wifi, Building, Copy, Lightbulb, History, AlertTriangle, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../components/ui/input';
import FloorPlanEditor from '../components/FloorPlanEditor';
import SiteImagesManager from '../components/SiteImagesManager';
import AdminEventsManager from '../components/AdminEventsManager';
import ClosedDaysManager from '../components/ClosedDaysManager';
import VouchersManager from '../components/VouchersManager';
import AnalyticsTab from '../components/admin/AnalyticsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [spaceFilter, setSpaceFilter] = useState('all');
  const [adminFreeFilter, setAdminFreeFilter] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [editingSpaces, setEditingSpaces] = useState({});
  const [editingSubs, setEditingSubs] = useState({});
  const [savingPrice, setSavingPrice] = useState(null);
  const [invoiceBookingId, setInvoiceBookingId] = useState(null);

  const getHeaders = useCallback(() => {
    const h = {};
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, usersRes, spacesRes, subsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/users`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/spaces`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/subscriptions`, { headers: getHeaders(), withCredentials: true })
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setUsers(usersRes.data);
      setSpaces(spacesRes.data);
      setSubscriptions(subsRes.data);
      setEditingSpaces({});
      setEditingSubs({});
    } catch (err) {
      console.error('Admin fetch error:', err);
      if (err.response?.status === 403) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== 'admin' && user.email !== 'dequenne@hotmail.com')) {
      navigate('/');
      return;
    }
    fetchAll();
  }, [user, authLoading, navigate, fetchAll]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true }
      );
      toast.success(`Reservation ${newStatus === 'confirmed' ? 'confirmee' : 'annulee'}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const resendNotification = async (bookingId) => {
    try {
      const res = await axios.post(`${API}/admin/send-notification/${bookingId}`, {},
        { headers: getHeaders(), withCredentials: true }
      );
      const r = res.data.results;
      toast.success(`Notification envoyee - Email: ${r.email ? 'OK' : 'Echec'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur d\'envoi');
    }
  };

  const refundBooking = async (bookingId, spaceName, price) => {
    if (!window.confirm(`Rembourser ${price} EUR pour "${spaceName}" ?\n\nLe client sera rembourse via Stripe et recevra un email de confirmation.`)) return;
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/refund`, {},
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success(`Remboursement de ${res.data.amount} EUR effectue avec succes`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de remboursement');
    }
  };

  const chargePenalty = async (bookingId, spaceName, existingInvoice) => {
    if (existingInvoice) {
      toast.info(`Une facture de pénalité existe déjà : ${existingInvoice}`);
      return;
    }
    const amountStr = window.prompt(
      `Facturer une pénalité de non-conformité au règlement pour "${spaceName}".\n\n` +
      `Montant TVAC (défaut 30 EUR) :`,
      '30'
    );
    if (amountStr === null) return;
    const amount = parseFloat(amountStr.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    const reason = window.prompt(
      'Motif de la pénalité (visible sur la facture) :',
      'Non-respect du règlement intérieur'
    );
    if (reason === null) return;
    try {
      const res = await axios.post(`${API}/admin/bookings/${bookingId}/penalty`,
        { amount_tvac: amount, reason: reason.trim() },
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success(`Facture ${res.data.invoice_number} émise (${amount.toFixed(2)} EUR) — email envoyé au client + comptable`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ? Ses reservations seront annulees.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`,
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success('Utilisateur supprime');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const viesLookupForUser = async (userId, userName) => {
    const vat = window.prompt(
      `Récupérer les données officielles depuis VIES pour "${userName}".\n\n` +
      `Saisissez le n° TVA (avec ou sans code pays) :`,
      ''
    );
    if (!vat || !vat.trim()) return;
    try {
      const lookup = await axios.get(`${API}/lookup/vat`, {
        params: { vat: vat.trim() },
        headers: getHeaders(),
        withCredentials: true,
      });
      if (!lookup.data?.valid) {
        toast.error('N° TVA invalide selon VIES');
        return;
      }
      const officialName = lookup.data.name || '';
      const parts = [lookup.data.address, lookup.data.postal_code, lookup.data.city]
        .map((s) => (s || '').trim())
        .filter(Boolean);
      const officialAddress = parts.join(', ');
      if (!window.confirm(
        `VIES a trouvé :\n\n` +
        `Raison sociale : ${officialName}\n` +
        `TVA : ${lookup.data.vat}\n` +
        `Adresse : ${officialAddress}\n\n` +
        `Écraser les données actuelles du client ?`
      )) return;
      await axios.put(`${API}/admin/users/${userId}/billing`, {
        company: officialName,
        company_vat: lookup.data.vat,
        address: officialAddress,
      }, { headers: getHeaders(), withCredentials: true });
      toast.success('Données B2B mises à jour depuis VIES');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la récupération VIES');
    }
  };

  const clearUserHistory = async (userId, userName) => {
    if (!window.confirm(
      `Effacer tout l'historique passé de "${userName}" ?\n\n` +
      `Toutes ses réservations avant aujourd'hui seront supprimées (récupérables 24h dans la corbeille).\n` +
      `Le compte reste actif — le client peut continuer à réserver.`
    )) return;
    try {
      const res = await axios.delete(`${API}/admin/users/${userId}/history`,
        { headers: getHeaders(), withCredentials: true }
      );
      const n = res.data?.deleted_bookings ?? 0;
      if (n === 0) {
        toast.info('Aucune réservation passée à effacer');
      } else {
        toast.success(`${n} réservation${n > 1 ? 's' : ''} passée${n > 1 ? 's' : ''} effacée${n > 1 ? 's' : ''}`);
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const startEditSpace = (spaceId, space) => {
    setEditingSpaces(prev => ({
      ...prev,
      [spaceId]: {
        price_hourly: space.price_hourly,
        price_half_day: space.price_half_day || 0,
        price_daily: space.price_daily,
        price_weekly: space.price_weekly,
        name_nl: space.name_nl || '',
        name_en: space.name_en || '',
        description_nl: space.description_nl || '',
        description_en: space.description_en || ''
      }
    }));
  };

  const cancelEditSpace = (spaceId) => {
    setEditingSpaces(prev => {
      const next = { ...prev };
      delete next[spaceId];
      return next;
    });
  };

  const saveSpacePrice = async (spaceId) => {
    const edit = editingSpaces[spaceId];
    if (!edit) return;
    setSavingPrice(spaceId);
    try {
      await axios.put(`${API}/admin/spaces/${spaceId}`, {
        price_hourly: parseFloat(edit.price_hourly),
        price_half_day: parseFloat(edit.price_half_day) || 0,
        price_daily: parseFloat(edit.price_daily),
        price_weekly: parseFloat(edit.price_weekly),
        name_nl: edit.name_nl || null,
        name_en: edit.name_en || null,
        description_nl: edit.description_nl || null,
        description_en: edit.description_en || null
      }, { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true });
      toast.success(`Tarifs de l'espace mis a jour`);
      cancelEditSpace(spaceId);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de mise a jour');
    } finally {
      setSavingPrice(null);
    }
  };

  const startEditSub = (planId, plan) => {
    setEditingSubs(prev => ({
      ...prev,
      [planId]: {
        price_monthly: plan.price_monthly,
        discount_percent: plan.discount_percent,
        name_nl: plan.name_nl || '',
        name_en: plan.name_en || '',
        features_nl: (plan.features_nl || []).join('\n'),
        features_en: (plan.features_en || []).join('\n')
      }
    }));
  };

  const cancelEditSub = (planId) => {
    setEditingSubs(prev => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
  };

  const saveSubPrice = async (planId) => {
    const edit = editingSubs[planId];
    if (!edit) return;
    setSavingPrice(planId);
    try {
      const featNl = (edit.features_nl || '').split('\n').map(s => s.trim()).filter(Boolean);
      const featEn = (edit.features_en || '').split('\n').map(s => s.trim()).filter(Boolean);
      await axios.put(`${API}/admin/subscriptions/${planId}`, {
        price_monthly: parseFloat(edit.price_monthly),
        discount_percent: parseFloat(edit.discount_percent),
        name_nl: edit.name_nl || null,
        name_en: edit.name_en || null,
        features_nl: featNl.length > 0 ? featNl : null,
        features_en: featEn.length > 0 ? featEn : null
      }, { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true });
      toast.success(`Abonnement mis a jour`);
      cancelEditSub(planId);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de mise a jour');
    } finally {
      setSavingPrice(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const filteredBookings = bookings.filter(b => {
    if (statusFilter !== 'all' && b.status !== statusFilter) return false;
    if (spaceFilter !== 'all' && b.space_id !== spaceFilter) return false;
    if (adminFreeFilter && !b.admin_free) return false;
    return true;
  });

  // Group multi-day reservations (linked by recurring_id) into a single "row"
  // so the admin sees ONE line "du 05/07 au 09/07 · 5 sessions" instead of
  // 5 separate rows. Single-day bookings pass through unchanged.
  const groupedBookings = (() => {
    const groups = new Map();
    const singles = [];
    for (const b of filteredBookings) {
      if (b.recurring_id) {
        const existing = groups.get(b.recurring_id);
        if (!existing) {
          groups.set(b.recurring_id, {
            ...b,
            booking_id: `group_${b.recurring_id}`,
            _is_group: true,
            _member_ids: [b.booking_id],
            _dates: [b.date],
            total_price: Number(b.total_price || 0),
          });
        } else {
          existing._member_ids.push(b.booking_id);
          existing._dates.push(b.date);
          existing.total_price += Number(b.total_price || 0);
        }
      } else {
        singles.push(b);
      }
    }
    for (const g of groups.values()) {
      g._dates.sort();
      g._date_start = g._dates[0];
      g._date_end = g._dates[g._dates.length - 1];
      g._count = g._member_ids.length;
      g.total_price = Math.round(g.total_price * 100) / 100;
    }
    const combined = [...singles, ...groups.values()];
    combined.sort((a, b) => {
      const da = a._is_group ? a._date_start : a.date;
      const db = b._is_group ? b._date_start : b.date;
      return String(db).localeCompare(String(da));
    });
    return combined;
  })();

  const adminFreeCount = bookings.filter(b => b.admin_free).length;

  const cleanupAdminFree = async () => {
    if (!window.confirm(`Mettre les ${adminFreeCount} réservation(s) "Gratuit (test admin)" à la corbeille ? Elles seront supprimées définitivement après 24 h.`)) return;
    try {
      const res = await axios.post(`${API}/admin/bookings/admin-free/cleanup`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(`${res.data.moved} réservation(s) déplacée(s) à la corbeille`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du nettoyage');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-[#C77D63]" />
              <h1 className="font-heading text-3xl font-semibold text-[#1A3C34]">
                Panel Admin
              </h1>
            </div>
            <p className="text-sm text-[#737373]">
              Gestion des reservations, utilisateurs et notifications
            </p>
          </div>
          <Button
            data-testid="admin-refresh-btn"
            variant="outline"
            onClick={fetchAll}
            className="border-[#E2E2DF] text-[#1A3C34] hover:bg-[#F0EFEA]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} color="bg-[#F0EFEA]" iconColor="text-[#1A3C34]" />
            <StatCard icon={Calendar} label="Reservations" value={stats.total_bookings} color="bg-green-50" iconColor="text-green-600" />
            <StatCard icon={TrendingUp} label="Revenu total" value={`${stats.total_revenue} EUR`} color="bg-[#C77D63]/10" iconColor="text-[#C77D63]" />
            <StatCard icon={CreditCard} label="Abonnes actifs" value={stats.active_subscriptions} color="bg-blue-50" iconColor="text-blue-600" />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-4" data-testid="admin-tabs">
          <TabsList
            className="bg-white border border-[#E2E2DF] flex flex-wrap h-auto gap-1 justify-start p-1.5"
            data-testid="admin-tabs-list"
          >
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">
              Reservations ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">
              Utilisateurs ({users.length})
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="admin-tab-pricing">
              Tarifs
            </TabsTrigger>
            <TabsTrigger value="floorplan" data-testid="admin-tab-floorplan">
              <Layout className="w-3 h-3 mr-1" /> Plan
            </TabsTrigger>
            <TabsTrigger value="images" data-testid="admin-tab-images">
              <ImagePlus className="w-3 h-3 mr-1" /> Images
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events">
              <Calendar className="w-3 h-3 mr-1" /> Evenements
            </TabsTrigger>
            <TabsTrigger value="closed" data-testid="admin-tab-closed">
              <Calendar className="w-3 h-3 mr-1" /> Fermetures
            </TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="admin-tab-vouchers">
              <Tag className="w-3 h-3 mr-1" /> Codes promo
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="admin-tab-invoices">
              <FileText className="w-3 h-3 mr-1" /> Factures
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests">
              <Mail className="w-3 h-3 mr-1" /> Demandes
            </TabsTrigger>
            <TabsTrigger value="access" data-testid="admin-tab-access">
              <Key className="w-3 h-3 mr-1" /> Accès porte
            </TabsTrigger>
            <TabsTrigger value="doors" data-testid="admin-tab-doors">
              <Shield className="w-3 h-3 mr-1" /> Portes
            </TabsTrigger>
            <TabsTrigger value="lights" data-testid="admin-tab-lights">
              <Lightbulb className="w-3 h-3 mr-1" /> Lumières
            </TabsTrigger>
            <TabsTrigger value="tenants" data-testid="admin-tab-tenants">
              <Building className="w-3 h-3 mr-1" /> Bureaux dédiés
            </TabsTrigger>
            <TabsTrigger value="kiosk" data-testid="admin-tab-kiosk">
              <Layout className="w-3 h-3 mr-1" /> Écran Kiosk
            </TabsTrigger>
            <TabsTrigger value="brochure" data-testid="admin-tab-brochure">
              <Download className="w-3 h-3 mr-1" /> Brochure — Leads
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">
              <BarChart3 className="w-3 h-3 mr-1" /> Visiteurs
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {/* Cleanup tool */}
            <CleanupBar
              kind="bookings"
              items={bookings}
              getHeaders={getHeaders}
              reload={fetchAll}
            />
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="admin-status-filter" className="w-40 h-10 border-[#E2E2DF] rounded-md">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="confirmed">Confirmees</SelectItem>
                  <SelectItem value="cancelled">Annulees</SelectItem>
                </SelectContent>
              </Select>
              <Select value={spaceFilter} onValueChange={setSpaceFilter}>
                <SelectTrigger data-testid="admin-space-filter" className="w-48 h-10 border-[#E2E2DF] rounded-md">
                  <SelectValue placeholder="Espace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les espaces</SelectItem>
                  {spaces.map(s => (
                    <SelectItem key={s.space_id} value={s.space_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant={adminFreeFilter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAdminFreeFilter(v => !v)}
                disabled={adminFreeCount === 0 && !adminFreeFilter}
                data-testid="admin-free-filter-btn"
                className={`h-10 ${adminFreeFilter ? 'bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90' : 'border-dashed border-[#1A3C34]/40 text-[#1A3C34]'}`}
              >
                🛡️ Tests admin {adminFreeCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#C77D63] text-white text-[10px] font-bold">{adminFreeCount}</span>}
              </Button>
              {adminFreeFilter && adminFreeCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cleanupAdminFree}
                  data-testid="cleanup-admin-free-btn"
                  className="h-10 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Tout mettre à la corbeille
                </Button>
              )}
            </div>

            {/* Bookings list */}
            <div className="space-y-3">
              {groupedBookings.length === 0 ? (
                <div className="bg-white border border-dashed border-[#E2E2DF] rounded-md p-12 text-center">
                  <Calendar className="w-8 h-8 text-[#E2E2DF] mx-auto mb-3" />
                  <p className="text-sm text-[#737373]">Aucune reservation</p>
                </div>
              ) : (
                groupedBookings.map((b) => (
                  <div
                    key={b.booking_id}
                    className="bg-white border border-[#E2E2DF] rounded-md p-4"
                    data-testid={`admin-booking-${b.booking_id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-body text-sm font-semibold text-[#1A3C34]">{b.space_name}</span>
                          <StatusBadge status={b.status} />
                          {b._is_group && (
                            <span
                              data-testid={`admin-booking-group-badge-${b.recurring_id}`}
                              className="text-[10px] uppercase tracking-wider bg-[#C77D63]/10 text-[#C77D63] px-1.5 py-0.5 rounded inline-flex items-center gap-1 border border-[#C77D63]/30"
                            >
                              📅 {b._count} sessions
                            </span>
                          )}
                          {b.admin_free && (
                            <span
                              data-testid={`admin-free-badge-${b.booking_id}`}
                              title={`Réservation gratuite (test) par ${b.admin_free_by || 'admin'}`}
                              className="text-[10px] uppercase tracking-wider bg-[#1A3C34]/10 text-[#1A3C34] px-1.5 py-0.5 rounded inline-flex items-center gap-1 border border-dashed border-[#1A3C34]/30"
                            >
                              🛡️ Test
                            </span>
                          )}
                          {b.access_code && (
                            <span className="font-mono text-xs bg-[#1A3C34] text-white px-2 py-0.5 rounded">
                              <Key className="w-3 h-3 inline mr-1" />
                              {b.access_code}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#737373] flex-wrap">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {b._is_group
                              ? `Du ${b._date_start} au ${b._date_end}`
                              : b.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {b.start_time} - {b.end_time}
                          </span>
                          <span className="font-mono font-semibold text-[#1A3C34]">{b.total_price} EUR</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#737373]">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" /> {b.user_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {b.user_email}
                          </span>
                          {b.user_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {b.user_phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {b.status === 'pending' && (
                          <Button
                            data-testid={`admin-confirm-${b.booking_id}`}
                            size="sm"
                            onClick={async () => {
                              const ids = b._is_group ? b._member_ids : [b.booking_id];
                              for (const id of ids) await updateBookingStatus(id, 'confirmed');
                            }}
                            className="bg-green-600 text-white hover:bg-green-700 h-8 px-3 text-xs"
                          >
                            <Check className="w-3 h-3 mr-1" /> Confirmer
                          </Button>
                        )}
                        {b.status !== 'cancelled' && (
                          <Button
                            data-testid={`admin-cancel-${b.booking_id}`}
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              const ids = b._is_group ? b._member_ids : [b.booking_id];
                              if (b._is_group && !window.confirm(
                                `Annuler les ${ids.length} sessions de cette réservation multi-jours ?`
                              )) return;
                              for (const id of ids) await updateBookingStatus(id, 'cancelled');
                            }}
                            className="border-red-300 text-red-600 hover:bg-red-50 h-8 px-3 text-xs"
                          >
                            <X className="w-3 h-3 mr-1" /> Annuler
                          </Button>
                        )}
                        {b.status === 'confirmed' && b.refund_status !== 'refunded' && (
                          <Button
                            data-testid={`admin-refund-${b.booking_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => refundBooking(
                              b._is_group ? b._member_ids[0] : b.booking_id,
                              b.space_name,
                              b.total_price
                            )}
                            className="border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/10 h-8 px-3 text-xs"
                          >
                            <RotateCcw className="w-3 h-3 mr-1" /> Rembourser
                          </Button>
                        )}
                        {b.refund_status === 'refunded' && (
                          <span className="text-[10px] font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">Rembourse</span>
                        )}
                        {b.status === 'confirmed' && b.refund_status !== 'refunded' && (
                          <Button
                            data-testid={`admin-penalty-${b.booking_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => chargePenalty(
                              b._is_group ? b._member_ids[0] : b.booking_id,
                              b.space_name,
                              b.penalty_invoice_number,
                            )}
                            className={b.penalty_invoice_number
                              ? 'border-amber-300 text-amber-700 hover:bg-amber-50 h-8 px-3 text-xs'
                              : 'border-red-300 text-red-600 hover:bg-red-50 h-8 px-3 text-xs'}
                            title={b.penalty_invoice_number
                              ? `Facture pénalité déjà émise: ${b.penalty_invoice_number}`
                              : 'Facturer une pénalité pour non-respect du règlement (30 EUR par défaut)'}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {b.penalty_invoice_number ? 'Pénalité ✓' : 'Pénalité 30€'}
                          </Button>
                        )}
                        {b.access_code && (
                          <Button
                            data-testid={`admin-resend-${b.booking_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => resendNotification(b._is_group ? b._member_ids[0] : b.booking_id)}
                            className="border-[#E2E2DF] text-[#1A3C34] hover:bg-[#F0EFEA] h-8 px-3 text-xs"
                          >
                            <Send className="w-3 h-3 mr-1" /> Renvoyer
                          </Button>
                        )}
                        {b.status === 'confirmed' && (
                          <Button
                            data-testid={`admin-invoice-${b.booking_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setInvoiceBookingId(b._is_group ? b._member_ids[0] : b.booking_id)}
                            className={`h-8 px-3 text-xs ${b.invoice_number ? 'border-green-200 text-green-700 hover:bg-green-50' : 'border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/10'}`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            {b.invoice_number ? `Facture ${b.invoice_number}` : 'Facture'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-3">
            {/* Cleanup tool */}
            <CleanupBar
              kind="users"
              items={users.filter(u => u.role !== 'admin')}
              getHeaders={getHeaders}
              reload={fetchAll}
            />
            {/* Unverified account summary — nudges admin to relance them
                before the 7-day auto-purge kicks in. */}
            {(() => {
              const unverified = users.filter(u => u.email_verified === false);
              if (unverified.length === 0) return null;
              return (
                <div
                  className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between gap-3"
                  data-testid="admin-unverified-summary"
                >
                  <div className="flex items-center gap-2 text-sm text-amber-900">
                    <span className="text-lg">✉️</span>
                    <span>
                      <strong>{unverified.length}</strong> inscription{unverified.length > 1 ? 's' : ''} en attente de confirmation d&apos;email (auto-suppression après 7 jours).
                    </span>
                  </div>
                </div>
              );
            })()}
            {users.map((u) => (
              <div
                key={u.user_id}
                className="bg-white border border-[#E2E2DF] rounded-md p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                data-testid={`admin-user-${u.user_id}`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full bg-[#1A3C34] flex items-center justify-center flex-shrink-0">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <span className="text-sm text-white font-medium">
                        {u.name?.charAt(0)?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-body text-sm font-semibold text-[#1A3C34] truncate">{u.name}</span>
                      {u.role === 'admin' && (
                        <Badge className="bg-[#C77D63]/10 text-[#C77D63] text-xs">Admin</Badge>
                      )}
                      {u.subscription?.status === 'active' && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {u.subscription.plan_name}
                        </Badge>
                      )}
                      {/* Email-verification pending badge — legacy accounts
                          without the field are treated as verified and get no
                          badge. Shows the days remaining before the 7-day
                          auto-purge picks them up. */}
                      {u.email_verified === false && (() => {
                        let daysLeft = 7;
                        try {
                          const created = new Date(u.created_at);
                          const elapsedMs = Date.now() - created.getTime();
                          daysLeft = Math.max(0, 7 - Math.floor(elapsedMs / 86400000));
                        } catch { /* fall through with default */ }
                        return (
                          <Badge
                            className="bg-amber-100 text-amber-800 text-xs"
                            data-testid={`admin-user-unverified-${u.user_id}`}
                            title={`Compte non confirmé. Sera supprimé dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}.`}
                          >
                            ✉️ Non confirmé · J-{daysLeft}
                          </Badge>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#737373] mt-1 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {u.email}
                      </span>
                      {u.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {u.phone}
                        </span>
                      )}
                      <span>{u.booking_count} reservation{u.booking_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
                {u.role !== 'admin' && (
                  <div className="flex items-center gap-1">
                    {u.email_verified === false && (
                      <Button
                        data-testid={`admin-resend-verify-${u.user_id}`}
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await axios.post(
                              `${API}/auth/resend-verification`,
                              { email: u.email },
                              { headers: getHeaders(), withCredentials: true },
                            );
                            toast.success(`Email de confirmation renvoyé à ${u.email}`);
                          } catch (e) {
                            toast.error('Renvoi impossible');
                          }
                        }}
                        className="text-amber-700 hover:bg-amber-50"
                        title="Renvoyer l'email de confirmation"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      data-testid={`admin-vies-${u.user_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => viesLookupForUser(u.user_id, u.name || u.email)}
                      className="text-[#1A3C34] hover:text-[#0f2621] hover:bg-[#1A3C34]/10"
                      title="Vérifier & pré-remplir les données B2B via VIES"
                    >
                      🔍
                    </Button>
                    <Button
                      data-testid={`admin-clear-history-${u.user_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => clearUserHistory(u.user_id, u.name || u.email)}
                      className="text-[#C77D63] hover:text-[#a5624a] hover:bg-[#C77D63]/10"
                      title="Effacer l'historique passé (compte gardé)"
                    >
                      <History className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`admin-delete-user-${u.user_id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteUser(u.user_id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      title="Supprimer le compte utilisateur"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {/* Blocked signup attempts — telemetry to refine the disposable-domain blocklist */}
            <BlockedSignupsPanel getHeaders={getHeaders} />
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="space-y-6" data-testid="admin-pricing-tab">
            {/* Space Pricing */}
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C77D63]" />
                Tarifs des espaces
              </h3>
              <div className="space-y-3">
                {spaces.map((space) => {
                  const editing = editingSpaces[space.space_id];
                  return (
                    <div
                      key={space.space_id}
                      className="bg-white border border-[#E2E2DF] rounded-md p-4"
                      data-testid={`admin-space-price-${space.space_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-shrink-0 min-w-[140px]">
                          <span className="font-body text-sm font-semibold text-[#1A3C34]">{space.name}</span>
                          <p className="text-xs text-[#737373]">{space.capacity} pers. | {space.area_m2} m2</p>
                        </div>
                        {editing ? (
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-[#737373] w-20">Heure</label>
                              <Input
                                type="number"
                                step="0.5"
                                data-testid={`price-hourly-${space.space_id}`}
                                value={editing.price_hourly}
                                onChange={(e) => setEditingSpaces(prev => ({
                                  ...prev,
                                  [space.space_id]: { ...prev[space.space_id], price_hourly: e.target.value }
                                }))}
                                className="w-20 h-8 text-sm border-[#E2E2DF]"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-[#737373] w-20">Demi-j (4h)</label>
                              <Input
                                type="number"
                                step="0.5"
                                data-testid={`price-half-day-${space.space_id}`}
                                value={editing.price_half_day}
                                onChange={(e) => setEditingSpaces(prev => ({
                                  ...prev,
                                  [space.space_id]: { ...prev[space.space_id], price_half_day: e.target.value }
                                }))}
                                className="w-20 h-8 text-sm border-[#E2E2DF]"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-[#737373] w-20">Jour</label>
                              <Input
                                type="number"
                                step="0.5"
                                data-testid={`price-daily-${space.space_id}`}
                                value={editing.price_daily}
                                onChange={(e) => setEditingSpaces(prev => ({
                                  ...prev,
                                  [space.space_id]: { ...prev[space.space_id], price_daily: e.target.value }
                                }))}
                                className="w-20 h-8 text-sm border-[#E2E2DF]"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-[#737373] w-20">Semaine</label>
                              <Input
                                type="number"
                                step="0.5"
                                data-testid={`price-weekly-${space.space_id}`}
                                value={editing.price_weekly}
                                onChange={(e) => setEditingSpaces(prev => ({
                                  ...prev,
                                  [space.space_id]: { ...prev[space.space_id], price_weekly: e.target.value }
                                }))}
                                className="w-24 h-8 text-sm border-[#E2E2DF]"
                              />
                            </div>
                            <div className="flex items-center gap-1 ml-auto">
                              <Button
                                size="sm"
                                data-testid={`save-space-${space.space_id}`}
                                onClick={() => saveSpacePrice(space.space_id)}
                                disabled={savingPrice === space.space_id}
                                className="bg-green-600 text-white hover:bg-green-700 h-8 px-3 text-xs"
                              >
                                <Save className="w-3 h-3 mr-1" />
                                {savingPrice === space.space_id ? '...' : 'Sauver'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelEditSpace(space.space_id)}
                                className="border-[#E2E2DF] h-8 px-3 text-xs"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                            </div>
                            <details className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-md px-3 py-2 text-xs">
                              <summary className="cursor-pointer text-[#1A3C34] font-medium select-none" data-testid={`space-translations-toggle-${space.space_id}`}>
                                Traductions NL / EN
                              </summary>
                              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-[#737373] block mb-1">Nom NL</label>
                                  <Input
                                    data-testid={`space-name-nl-${space.space_id}`}
                                    value={editing.name_nl}
                                    onChange={(e) => setEditingSpaces(prev => ({
                                      ...prev,
                                      [space.space_id]: { ...prev[space.space_id], name_nl: e.target.value }
                                    }))}
                                    className="h-8 text-sm border-[#E2E2DF]"
                                    placeholder="Grote Zaal"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-[#737373] block mb-1">Nom EN</label>
                                  <Input
                                    data-testid={`space-name-en-${space.space_id}`}
                                    value={editing.name_en}
                                    onChange={(e) => setEditingSpaces(prev => ({
                                      ...prev,
                                      [space.space_id]: { ...prev[space.space_id], name_en: e.target.value }
                                    }))}
                                    className="h-8 text-sm border-[#E2E2DF]"
                                    placeholder="Main Hall"
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <label className="text-xs text-[#737373] block mb-1">Description NL</label>
                                  <textarea
                                    data-testid={`space-desc-nl-${space.space_id}`}
                                    value={editing.description_nl}
                                    onChange={(e) => setEditingSpaces(prev => ({
                                      ...prev,
                                      [space.space_id]: { ...prev[space.space_id], description_nl: e.target.value }
                                    }))}
                                    rows={2}
                                    className="w-full text-sm border border-[#E2E2DF] rounded px-2 py-1 bg-white"
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <label className="text-xs text-[#737373] block mb-1">Description EN</label>
                                  <textarea
                                    data-testid={`space-desc-en-${space.space_id}`}
                                    value={editing.description_en}
                                    onChange={(e) => setEditingSpaces(prev => ({
                                      ...prev,
                                      [space.space_id]: { ...prev[space.space_id], description_en: e.target.value }
                                    }))}
                                    rows={2}
                                    className="w-full text-sm border border-[#E2E2DF] rounded px-2 py-1 bg-white"
                                  />
                                </div>
                              </div>
                            </details>
                          </div>
                        ) : (
                          <div className="flex items-center gap-4 flex-wrap flex-1">
                            <span className="font-mono text-sm text-[#1A3C34]">{space.price_hourly} EUR/h HT</span>
                            {space.price_half_day > 0 && (
                              <span className="font-mono text-sm text-[#1A3C34]" data-testid={`display-half-day-${space.space_id}`}>
                                {space.price_half_day} EUR/4h HT
                              </span>
                            )}
                            <span className="font-mono text-sm text-[#1A3C34]">{space.price_daily} EUR/j HT</span>
                            <span className="font-mono text-sm text-[#1A3C34]">{space.price_weekly} EUR/sem HT</span>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-space-${space.space_id}`}
                              onClick={() => startEditSpace(space.space_id, space)}
                              className="border-[#E2E2DF] text-[#1A3C34] hover:bg-[#F0EFEA] h-8 px-3 text-xs ml-auto"
                            >
                              <Edit3 className="w-3 h-3 mr-1" /> Modifier
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Subscription Pricing */}
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#1A3C34] mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#C77D63]" />
                Tarifs des abonnements
              </h3>
              <div className="space-y-3">
                {subscriptions.map((sub) => {
                  const editing = editingSubs[sub.plan_id];
                  return (
                    <div
                      key={sub.plan_id}
                      className="bg-white border border-[#E2E2DF] rounded-md p-4"
                      data-testid={`admin-sub-price-${sub.plan_id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-shrink-0 min-w-[160px]">
                          <span className="font-body text-sm font-semibold text-[#1A3C34]">{sub.name}</span>
                          <p className="text-xs text-[#737373]">{sub.duration === 'monthly' ? 'Mensuel' : 'Annuel'}</p>
                        </div>
                        {editing ? (
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-[#737373] w-16">Prix/mois</label>
                              <Input
                                type="number"
                                step="0.5"
                                data-testid={`sub-price-${sub.plan_id}`}
                                value={editing.price_monthly}
                                onChange={(e) => setEditingSubs(prev => ({
                                  ...prev,
                                  [sub.plan_id]: { ...prev[sub.plan_id], price_monthly: e.target.value }
                                }))}
                                className="w-20 h-8 text-sm border-[#E2E2DF]"
                              />
                              <span className="text-xs text-[#737373]">EUR</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-[#737373] w-16">Remise</label>
                              <Input
                                type="number"
                                step="0.5"
                                data-testid={`sub-discount-${sub.plan_id}`}
                                value={editing.discount_percent}
                                onChange={(e) => setEditingSubs(prev => ({
                                  ...prev,
                                  [sub.plan_id]: { ...prev[sub.plan_id], discount_percent: e.target.value }
                                }))}
                                className="w-16 h-8 text-sm border-[#E2E2DF]"
                              />
                              <span className="text-xs text-[#737373]">%</span>
                            </div>
                            <div className="flex items-center gap-1 ml-auto">
                              <Button
                                size="sm"
                                data-testid={`save-sub-${sub.plan_id}`}
                                onClick={() => saveSubPrice(sub.plan_id)}
                                disabled={savingPrice === sub.plan_id}
                                className="bg-green-600 text-white hover:bg-green-700 h-8 px-3 text-xs"
                              >
                                <Save className="w-3 h-3 mr-1" />
                                {savingPrice === sub.plan_id ? '...' : 'Sauver'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelEditSub(sub.plan_id)}
                                className="border-[#E2E2DF] h-8 px-3 text-xs"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                            </div>
                            <details className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-md px-3 py-2 text-xs">
                              <summary className="cursor-pointer text-[#1A3C34] font-medium select-none" data-testid={`sub-translations-toggle-${sub.plan_id}`}>
                                Traductions NL / EN (nom + features, une par ligne)
                              </summary>
                              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs text-[#737373] block mb-1">Nom NL</label>
                                  <Input
                                    data-testid={`sub-name-nl-${sub.plan_id}`}
                                    value={editing.name_nl}
                                    onChange={(e) => setEditingSubs(prev => ({
                                      ...prev,
                                      [sub.plan_id]: { ...prev[sub.plan_id], name_nl: e.target.value }
                                    }))}
                                    className="h-8 text-sm border-[#E2E2DF]"
                                    placeholder="Maandabonnement"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-[#737373] block mb-1">Nom EN</label>
                                  <Input
                                    data-testid={`sub-name-en-${sub.plan_id}`}
                                    value={editing.name_en}
                                    onChange={(e) => setEditingSubs(prev => ({
                                      ...prev,
                                      [sub.plan_id]: { ...prev[sub.plan_id], name_en: e.target.value }
                                    }))}
                                    className="h-8 text-sm border-[#E2E2DF]"
                                    placeholder="Monthly Subscription"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-[#737373] block mb-1">Features NL</label>
                                  <textarea
                                    data-testid={`sub-features-nl-${sub.plan_id}`}
                                    value={editing.features_nl}
                                    onChange={(e) => setEditingSubs(prev => ({
                                      ...prev,
                                      [sub.plan_id]: { ...prev[sub.plan_id], features_nl: e.target.value }
                                    }))}
                                    rows={5}
                                    className="w-full text-sm border border-[#E2E2DF] rounded px-2 py-1 bg-white font-mono"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-[#737373] block mb-1">Features EN</label>
                                  <textarea
                                    data-testid={`sub-features-en-${sub.plan_id}`}
                                    value={editing.features_en}
                                    onChange={(e) => setEditingSubs(prev => ({
                                      ...prev,
                                      [sub.plan_id]: { ...prev[sub.plan_id], features_en: e.target.value }
                                    }))}
                                    rows={5}
                                    className="w-full text-sm border border-[#E2E2DF] rounded px-2 py-1 bg-white font-mono"
                                  />
                                </div>
                              </div>
                            </details>
                          </div>
                        ) : (
                          <div className="flex items-center gap-4 flex-wrap flex-1">
                            <span className="font-mono text-sm text-[#1A3C34]">{sub.price_monthly} EUR/mois</span>
                            <Badge className="bg-green-50 text-green-700 text-xs">-{sub.discount_percent}%</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-sub-${sub.plan_id}`}
                              onClick={() => startEditSub(sub.plan_id, sub)}
                              className="border-[#E2E2DF] text-[#1A3C34] hover:bg-[#F0EFEA] h-8 px-3 text-xs ml-auto"
                            >
                              <Edit3 className="w-3 h-3 mr-1" /> Modifier
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Floor Plan Tab */}
          <TabsContent value="floorplan" data-testid="admin-floorplan-tab">
            <FloorPlanEditor spaces={spaces} getHeaders={getHeaders} onSpacesChanged={fetchAll} />
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images" data-testid="admin-images-tab">
            <SiteImagesManager spaces={spaces} getHeaders={getHeaders} onSpacesChanged={fetchAll} />
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" data-testid="admin-events-tab">
            <AdminEventsManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Closed Days Tab */}
          <TabsContent value="closed" data-testid="admin-closed-tab">
            <ClosedDaysManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Vouchers Tab */}
          <TabsContent value="vouchers" data-testid="admin-vouchers-tab">
            <VouchersManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" data-testid="admin-invoices-tab">
            <ClearFactsSettingsCard getHeaders={getHeaders} />
            <AdminInvoicesManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Partnership Requests Tab */}
          <TabsContent value="requests" data-testid="admin-requests-tab">
            <AdminPartnershipRequestsManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Door access logs Tab */}
          <TabsContent value="access" data-testid="admin-access-tab">
            <AdminAccessLogsManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Doors (Raspberry devices) Tab */}
          <TabsContent value="doors" data-testid="admin-doors-tab">
            <AdminDoorDevicesManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Lights Tab — grouped lighting controls (timing + per-space test) */}
          <TabsContent value="lights" data-testid="admin-lights-tab">
            <AdminLightsManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Dedicated tenants Tab */}
          <TabsContent value="tenants" data-testid="admin-tenants-tab">
            <AdminTenantsManager getHeaders={getHeaders} />
          </TabsContent>

          {/* Kiosk ads Tab */}
          <TabsContent value="kiosk" data-testid="admin-kiosk-tab">
            <AdminKioskAdsManager getHeaders={getHeaders} />
          </TabsContent>

          <TabsContent value="brochure" data-testid="admin-brochure-tab">
            <AdminBrochureLeads getHeaders={getHeaders} />
          </TabsContent>

          <TabsContent value="analytics" data-testid="admin-analytics-tab">
            <AnalyticsTab />
          </TabsContent>
        </Tabs>

        {/* Invoice modal (supplements + force generate) */}
        {invoiceBookingId && (
          <BookingInvoiceModal
            bookingId={invoiceBookingId}
            getHeaders={getHeaders}
            onClose={() => setInvoiceBookingId(null)}
            onGenerated={() => { setInvoiceBookingId(null); fetchAll(); }}
          />
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, iconColor }) {
  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-md ${color} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <div className="font-mono text-xl font-semibold text-[#1A3C34]">{value}</div>
          <div className="text-xs text-[#737373]">{label}</div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    confirmed: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-gray-100 text-gray-500'
  };
  const labels = {
    confirmed: 'Confirmee',
    pending: 'En attente',
    cancelled: 'Annulee'
  };
  return (
    <Badge className={`text-xs ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
      {labels[status] || status}
    </Badge>
  );
}


// ============================================================
// ADMIN INVOICES MANAGER
// ============================================================
function AdminInvoicesManager({ getHeaders }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (yearFilter) params.year = yearFilter;
      const res = await axios.get(`${API}/admin/invoices`, {
        headers: getHeaders(),
        withCredentials: true,
        params,
      });
      setInvoices(res.data || []);
    } catch (e) {
      console.error('Admin invoices fetch error:', e);
      toast.error('Erreur de chargement des factures');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, yearFilter]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (invoiceNumber) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoiceNumber}/pdf`, {
        headers: getHeaders(),
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Téléchargement impossible');
    }
  };

  const resyncBilling = async (invoiceNumber, customerName) => {
    const ok = window.confirm(
      `Recharger les données de facturation (société, TVA, BCE, adresse) du client "${customerName}" sur la facture ${invoiceNumber} ?\n\n` +
      "⚠️ À n'utiliser que si la facture n'a pas encore été envoyée au client ou au comptable. " +
      "Une facture déjà transmise ne peut légalement pas être modifiée (art. 53 CTVA) — émettez un avoir à la place."
    );
    if (!ok) return;
    try {
      const res = await axios.post(
        `${API}/admin/invoices/${invoiceNumber}/resync-billing`,
        {},
        { headers: getHeaders(), withCredentials: true },
      );
      const c = res.data?.customer || {};
      const bits = [c.company, c.vat && `TVA ${c.vat}`, c.bce && `BCE ${c.bce}`, c.address]
        .filter(Boolean).join(' • ');
      toast.success(`Facture ${invoiceNumber} mise à jour : ${bits || 'aucune donnée B2B'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resync impossible');
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (yearFilter) params.year = yearFilter;
      const res = await axios.get(`${API}/admin/invoices/export-csv`, {
        headers: getHeaders(),
        withCredentials: true,
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factures_${yearFilter || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (e) {
      toast.error('Export CSV impossible');
    }
  };

  const exportLivreCaisseCsv = async (quarter) => {
    try {
      const params = { year: yearFilter || new Date().getFullYear() };
      if (quarter) params.quarter = quarter;
      const res = await axios.get(`${API}/admin/receipts/export-csv`, {
        headers: getHeaders(),
        withCredentials: true,
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `livre_caisse_${params.year}${quarter ? `_${quarter}` : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Livre de caisse ${quarter || 'annuel'} téléchargé`);
    } catch (e) {
      toast.error('Export livre de caisse impossible');
    }
  };

  const filtered = invoices.filter((inv) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (inv.invoice_number || '').toLowerCase().includes(q) ||
      (inv.customer?.email || '').toLowerCase().includes(q) ||
      (inv.customer?.name || '').toLowerCase().includes(q)
    );
  });

  // Totals across filtered set
  const totalTvac = filtered.reduce((s, i) => s + (i.totals?.tvac || 0), 0);
  const totalHt = filtered.reduce((s, i) => s + (i.totals?.ht || 0), 0);
  const totalTva = filtered.reduce((s, i) => s + (i.totals?.tva || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-invoices-content">
      <div className="flex flex-wrap gap-3 items-center">
        <Input
          placeholder="Année (ex: 2026)"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value.replace(/[^0-9]/g, ''))}
          className="w-40 h-10 border-[#E2E2DF] rounded-md"
          data-testid="invoices-year-filter"
        />
        <Input
          placeholder="Rechercher (numéro, email, nom)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72 h-10 border-[#E2E2DF] rounded-md"
          data-testid="invoices-search"
        />
        <Button
          variant="outline"
          onClick={load}
          data-testid="invoices-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
        <Button
          onClick={exportCsv}
          className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
          data-testid="invoices-export-csv-btn"
        >
          <Download className="w-4 h-4 mr-1" /> Export factures
        </Button>
        {/* Livre de caisse — quarterly B2C receipts export */}
        <div className="flex items-center gap-1 border border-[#C77D63]/30 rounded-md px-2 py-1 bg-[#C77D63]/5">
          <span className="text-xs font-semibold text-[#C77D63] uppercase mr-1">Livre caisse</span>
          {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
            <Button
              key={q}
              size="sm"
              variant="ghost"
              onClick={() => exportLivreCaisseCsv(q)}
              className="h-7 px-2 text-xs text-[#C77D63] hover:bg-[#C77D63]/10"
              data-testid={`livre-caisse-${q}-btn`}
              title={`Export livre de caisse ${q} ${yearFilter || new Date().getFullYear()}`}
            >
              {q}
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => exportLivreCaisseCsv(null)}
            className="h-7 px-2 text-xs text-[#C77D63] hover:bg-[#C77D63]/10"
            data-testid="livre-caisse-year-btn"
            title={`Export livre de caisse annuel ${yearFilter || new Date().getFullYear()}`}
          >
            Année
          </Button>
        </div>
      </div>

      {/* Totals summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
          <p className="text-xs text-[#737373]">Nombre de factures</p>
          <p className="text-2xl font-semibold text-[#1A3C34]" data-testid="invoices-count">{filtered.length}</p>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
          <p className="text-xs text-[#737373]">Total HTVA</p>
          <p className="text-2xl font-semibold text-[#1A3C34]">{totalHt.toFixed(2)} €</p>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
          <p className="text-xs text-[#737373]">Total TVA</p>
          <p className="text-2xl font-semibold text-[#1A3C34]">{totalTva.toFixed(2)} €</p>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4 bg-[#F2EDE6]">
          <p className="text-xs text-[#737373]">Total TVAC</p>
          <p className="text-2xl font-semibold text-[#1A3C34]" data-testid="invoices-total-tvac">{totalTvac.toFixed(2)} €</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F7] text-[#737373]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">N°</th>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Client</th>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-right px-3 py-2 font-medium">HTVA</th>
                <th className="text-right px-3 py-2 font-medium">TVA</th>
                <th className="text-right px-3 py-2 font-medium">TVAC</th>
                <th className="text-left px-3 py-2 font-medium">Méthode</th>
                <th className="text-right px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="text-center py-8 text-[#737373]">Chargement…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-8 text-[#737373]">Aucune facture</td></tr>
              ) : filtered.map((inv) => (
                <tr key={inv.invoice_number} className="border-t border-[#E2E2DF]" data-testid={`admin-invoice-row-${inv.invoice_number}`}>
                  <td className="px-3 py-2 font-mono text-[#1A3C34]">{inv.invoice_number}</td>
                  <td className="px-3 py-2 text-[#1A3C34]">{inv.issued_at_display}</td>
                  <td className="px-3 py-2 text-[#1A3C34]">{inv.customer?.name || '—'}</td>
                  <td className="px-3 py-2 text-[#737373]">{inv.customer?.email || '—'}</td>
                  <td className="px-3 py-2 text-right">{(inv.totals?.ht || 0).toFixed(2)} €</td>
                  <td className="px-3 py-2 text-right">{(inv.totals?.tva || 0).toFixed(2)} €</td>
                  <td className="px-3 py-2 text-right font-medium text-[#1A3C34]">{(inv.totals?.tvac || 0).toFixed(2)} €</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="capitalize">{inv.payment_method || '—'}</Badge></td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resyncBilling(inv.invoice_number, inv.customer?.name || '—')}
                        data-testid={`admin-resync-${inv.invoice_number}`}
                        title="Recharger les données de facturation du client (société / TVA / BCE / adresse) — correction technique, si non envoyée."
                        className="border-[#C77D63]/40 text-[#C77D63] hover:bg-[#C77D63]/10"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Resync
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadPdf(inv.invoice_number)}
                        data-testid={`admin-download-${inv.invoice_number}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ADMIN PARTNERSHIP REQUESTS MANAGER
// ============================================================
function AdminPartnershipRequestsManager({ getHeaders }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/admin/partnership-requests`, {
        headers: getHeaders(),
        withCredentials: true,
        params,
      });
      setItems(res.data || []);
    } catch (e) {
      console.error('Partnership requests fetch error:', e);
      toast.error('Erreur de chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/partnership-requests/${id}/status`,
        { status: newStatus },
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success('Statut mis à jour');
      load();
    } catch (e) {
      toast.error('Mise à jour impossible');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    try {
      await axios.delete(`${API}/admin/partnership-requests/${id}`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      toast.success('Demande supprimée');
      load();
    } catch (e) {
      toast.error('Suppression impossible');
    }
  };

  const typeLabels = {
    add_address: 'Nouvelle adresse',
    clone: 'Dupliquer le site',
    affiliate: 'Affiliation',
    '': 'Demande générale',
  };
  const statusBadgeClass = {
    new: 'bg-orange-100 text-orange-700',
    in_progress: 'bg-blue-100 text-blue-700',
    done: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  const statusLabels = {
    new: 'Nouvelle',
    in_progress: 'En cours',
    done: 'Traitée',
    archived: 'Archivée',
  };

  const newCount = items.filter((i) => (i.status || 'new') === 'new').length;

  return (
    <div className="space-y-4" data-testid="admin-requests-content">
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 h-10 border-[#E2E2DF] rounded-md" data-testid="requests-status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="new">Nouvelle</SelectItem>
            <SelectItem value="in_progress">En cours</SelectItem>
            <SelectItem value="done">Traitée</SelectItem>
            <SelectItem value="archived">Archivée</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="requests-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
        {newCount > 0 && (
          <Badge className="bg-orange-100 text-orange-700">{newCount} nouvelle{newCount > 1 ? 's' : ''}</Badge>
        )}
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
        {loading ? (
          <div className="text-center py-10 text-[#737373]">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-[#737373]">Aucune demande</div>
        ) : (
          <ul className="divide-y divide-[#E2E2DF]">
            {items.map((req) => {
              const isOpen = expandedId === req.request_id;
              const status = req.status || 'new';
              return (
                <li key={req.request_id} className="p-4" data-testid={`request-item-${req.request_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-medium text-[#1A3C34]">{req.name}</span>
                        <Badge variant="outline" className="text-xs">{typeLabels[req.type || ''] || req.type || '—'}</Badge>
                        <Badge className={`text-xs ${statusBadgeClass[status] || 'bg-gray-100 text-gray-500'}`}>
                          {statusLabels[status] || status}
                        </Badge>
                      </div>
                      <div className="text-sm text-[#737373] flex flex-wrap gap-x-4 gap-y-1">
                        <span><Mail className="w-3 h-3 inline mr-1" /><a href={`mailto:${req.email}`} className="hover:text-[#1A3C34]">{req.email}</a></span>
                        {req.phone && <span><Phone className="w-3 h-3 inline mr-1" />{req.phone}</span>}
                        <span><Clock className="w-3 h-3 inline mr-1" />{(req.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                      </div>
                      {isOpen ? (
                        <p className="mt-3 text-sm text-[#1A3C34] whitespace-pre-wrap bg-[#F9F9F7] p-3 rounded-md">
                          {req.message}
                        </p>
                      ) : (
                        <p className="mt-2 text-sm text-[#737373] line-clamp-1 max-w-2xl">
                          {req.message}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedId(isOpen ? null : req.request_id)}
                        data-testid={`request-toggle-${req.request_id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        {isOpen ? 'Masquer' : 'Voir'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`mailto:${req.email}?subject=Re: votre demande Flexi-location`, '_blank')}
                        data-testid={`request-reply-${req.request_id}`}
                      >
                        <Send className="w-3.5 h-3.5 mr-1" /> Répondre
                      </Button>
                      <Select value={status} onValueChange={(v) => updateStatus(req.request_id, v)}>
                        <SelectTrigger className="w-36 h-9 border-[#E2E2DF] rounded-md text-xs" data-testid={`request-status-${req.request_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Nouvelle</SelectItem>
                          <SelectItem value="in_progress">En cours</SelectItem>
                          <SelectItem value="done">Traitée</SelectItem>
                          <SelectItem value="archived">Archivée</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => remove(req.request_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        data-testid={`request-delete-${req.request_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}


// ============================================================
// BOOKING INVOICE MODAL — supplements + force-generate
// ============================================================
function BookingInvoiceModal({ bookingId, getHeaders, onClose, onGenerated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [supplements, setSupplements] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings/${bookingId}/invoice-preview`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      setData(res.data);
      setSupplements((res.data?.supplements || []).map((s) => ({
        ...s,
        _id: s.supplement_id || `loc_${Math.random().toString(36).slice(2, 8)}`,
      })));
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [bookingId, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const isLocked = Boolean(data?.invoice_number);

  const addSupplement = () => {
    setSupplements((arr) => [...arr, { _id: `loc_${Math.random().toString(36).slice(2, 8)}`, description: '', amount_tvac: 0 }]);
  };
  const updateSupplement = (id, field, value) => {
    setSupplements((arr) => arr.map((s) => (s._id === id ? { ...s, [field]: value } : s)));
  };
  const removeSupplement = (id) => {
    setSupplements((arr) => arr.filter((s) => s._id !== id));
  };

  const saveSupplements = async () => {
    setSaving(true);
    try {
      const cleaned = supplements
        .map((s) => ({
          description: (s.description || '').trim(),
          amount_tvac: Number(s.amount_tvac) || 0,
          supplement_id: s.supplement_id,
        }))
        .filter((s) => s.description && s.amount_tvac !== 0);
      await axios.put(
        `${API}/admin/bookings/${bookingId}/supplements`,
        { supplements: cleaned },
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success('Suppléments enregistrés');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async () => {
    if (!window.confirm('Générer la facture maintenant et l\'envoyer par email ?\nLes suppléments ne pourront plus être modifiés après.')) return;
    setGenerating(true);
    try {
      // Save supplements first (in case user changed something)
      await saveSupplements();
      const res = await axios.post(
        `${API}/admin/bookings/${bookingId}/generate-invoice`,
        {},
        { headers: getHeaders(), withCredentials: true },
      );
      toast.success(`Facture ${res.data.invoice_number} générée et envoyée (${res.data.totals.tvac.toFixed(2)} € TVAC)`);
      onGenerated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur génération facture');
    } finally {
      setGenerating(false);
    }
  };

  // Live total preview
  const baseTvac = Number(data?.total_price || 0);
  const supTvac = supplements.reduce((s, x) => s + (Number(x.amount_tvac) || 0), 0);
  const totalTvac = baseTvac + supTvac;
  const totalHt = totalTvac / 1.21;
  const totalTva = totalTvac - totalHt;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="booking-invoice-modal">
        <div className="bg-[#1A3C34] text-white px-5 py-4 flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Facture pour réservation
          </h3>
          <button onClick={onClose} className="hover:opacity-70" data-testid="invoice-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#737373]">Chargement…</div>
        ) : !data ? (
          <div className="p-8 text-center text-[#737373]">Réservation introuvable</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Status banner */}
            {isLocked ? (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-3 text-sm">
                ✅ Facture <strong>{data.invoice_number}</strong> déjà générée
                {data.invoice_generated_at && <> — le {new Date(data.invoice_generated_at).toLocaleString('fr-BE')}</>}
                <p className="text-xs mt-1 text-green-700">Les suppléments sont verrouillés. Pour modifier, il faut créer un avoir comptable.</p>
              </div>
            ) : (
              <div className="bg-[#FFF8F0] border border-[#C77D63]/30 text-[#8B5A3C] rounded-md p-3 text-sm">
                <strong>Facture à générer.</strong> La génération automatique est prévue 1h après la fin de la location ({data.end_date} {data.end_time}).
                <br />Vous pouvez ajouter des frais additionnels (heure supplémentaire, dégâts, remises…) avant la génération.
              </div>
            )}

            {/* Base price */}
            <div className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-md p-3 text-sm flex justify-between">
              <span className="text-[#737373]">Prix de la location (déjà payé)</span>
              <span className="font-semibold text-[#1A3C34]">{baseTvac.toFixed(2)} € TVAC</span>
            </div>

            {/* Supplements list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[#1A3C34]">Frais additionnels</label>
                {!isLocked && (
                  <Button size="sm" variant="outline" onClick={addSupplement} data-testid="add-supplement-btn">
                    + Ajouter
                  </Button>
                )}
              </div>
              {supplements.length === 0 ? (
                <p className="text-xs text-[#737373] italic">Aucun frais additionnel.</p>
              ) : (
                <div className="space-y-2">
                  {supplements.map((s, idx) => (
                    <div key={s._id} className="flex gap-2 items-start" data-testid={`supplement-row-${idx}`}>
                      <Input
                        value={s.description}
                        onChange={(e) => updateSupplement(s._id, 'description', e.target.value)}
                        placeholder="Description (ex: Heure supplémentaire)"
                        disabled={isLocked}
                        className="flex-1 h-9 border-[#E2E2DF] rounded-md text-sm"
                        data-testid={`supplement-desc-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={s.amount_tvac}
                        onChange={(e) => updateSupplement(s._id, 'amount_tvac', e.target.value)}
                        placeholder="0.00"
                        disabled={isLocked}
                        className="w-28 h-9 border-[#E2E2DF] rounded-md text-sm font-mono text-right"
                        data-testid={`supplement-amt-${idx}`}
                      />
                      <span className="text-xs text-[#737373] self-center">€ TVAC</span>
                      {!isLocked && (
                        <button
                          onClick={() => removeSupplement(s._id)}
                          className="text-red-500 hover:text-red-700 self-center"
                          data-testid={`supplement-del-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-[#737373] mt-2">
                Astuce : laissez vide si la location s'est déroulée normalement. Utilisez un montant négatif pour appliquer une remise commerciale.
              </p>
            </div>

            {/* Totals preview */}
            <div className="border-t border-[#E2E2DF] pt-3 space-y-1 text-sm">
              <div className="flex justify-between text-[#737373]">
                <span>Total HTVA</span>
                <span>{totalHt.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-[#737373]">
                <span>TVA 21 %</span>
                <span>{totalTva.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-base font-semibold text-[#1A3C34] pt-1">
                <span>TOTAL TVAC</span>
                <span data-testid="invoice-modal-total">{totalTvac.toFixed(2)} €</span>
              </div>
            </div>

            {/* Actions */}
            {!isLocked && (
              <div className="flex flex-wrap gap-2 justify-end pt-3 border-t border-[#E2E2DF]">
                <Button
                  variant="outline"
                  onClick={saveSupplements}
                  disabled={saving || generating}
                  data-testid="save-supplements-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
                <Button
                  onClick={generateNow}
                  disabled={generating || saving}
                  className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
                  data-testid="generate-invoice-now-btn"
                >
                  <Send className="w-4 h-4 mr-1" />
                  {generating ? 'Génération…' : 'Générer & envoyer maintenant'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================
// ADMIN ACCESS LOGS (Raspberry door)
// ============================================================
function AdminAccessLogsManager({ getHeaders }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyFailures, setOnlyFailures] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/access-logs`, {
        headers: getHeaders(),
        withCredentials: true,
        params: { limit: 200, only_failures: onlyFailures },
      });
      setLogs(res.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, onlyFailures]);

  useEffect(() => { load(); }, [load]);

  const deleteLog = async (logId) => {
    if (!logId) return;
    if (!window.confirm('Supprimer définitivement cette entrée du journal ?')) return;
    setDeletingId(logId);
    try {
      await axios.delete(`${API}/admin/access-logs/${logId}`, {
        headers: getHeaders(), withCredentials: true,
      });
      setLogs((prev) => prev.filter((l) => l.id !== logId));
      toast.success('Entrée supprimée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    } finally {
      setDeletingId(null);
    }
  };

  const reasonLabel = {
    ok: 'OK',
    invalid_code: 'Code inconnu',
    wrong_time: 'Hors créneau',
    wrong_date: 'Mauvaise date',
  };
  const reasonClass = {
    ok: 'bg-green-100 text-green-700',
    invalid_code: 'bg-red-100 text-red-700',
    wrong_time: 'bg-orange-100 text-orange-700',
    wrong_date: 'bg-orange-100 text-orange-700',
  };

  const successCount = logs.filter((l) => l.access).length;
  const failCount = logs.length - successCount;

  return (
    <div className="space-y-4" data-testid="admin-access-content">
      <div className="bg-[#FFF8F0] border border-[#C77D63]/30 rounded-md p-4 text-sm text-[#1A3C34]">
        <div className="flex items-start gap-2">
          <Key className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Configuration du Raspberry Pi</p>
            <p className="text-xs text-[#737373] mt-1">
              <strong>URL :</strong> <code className="bg-white px-1 py-0.5 rounded">POST {window.location.origin.replace('preview.emergentagent.com', '').includes('flexi-location') ? 'https://flexi-location.be' : window.location.origin}/api/access/verify</code><br />
              <strong>En-têtes requis :</strong> <code className="bg-white px-1 py-0.5 rounded">X-API-Key: votre_clé_API</code> + <code className="bg-white px-1 py-0.5 rounded">Content-Type: application/json</code><br />
              <strong>Corps :</strong> <code className="bg-white px-1 py-0.5 rounded">{`{"code":"1234"}`}</code><br />
              <strong>Réponse :</strong> <code className="bg-white px-1 py-0.5 rounded">{`{"access":true|false,"reason":"ok|wrong_time|wrong_date|invalid_code","valid_until":"..."}`}</code>
            </p>
            <p className="text-xs text-[#737373] mt-2">
              💡 La clé API se trouve dans <code className="bg-white px-1 rounded">backend/.env → DOOR_API_KEY</code>. Tolérance : ±15 min autour du créneau.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Button
          variant={onlyFailures ? 'outline' : 'default'}
          size="sm"
          onClick={() => setOnlyFailures(false)}
          data-testid="filter-all-btn"
          className={!onlyFailures ? 'bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90' : ''}
        >
          Toutes ({logs.length})
        </Button>
        <Button
          variant={onlyFailures ? 'default' : 'outline'}
          size="sm"
          onClick={() => setOnlyFailures(true)}
          data-testid="filter-failures-btn"
          className={onlyFailures ? 'bg-red-600 text-white hover:bg-red-700' : 'border-red-200 text-red-600 hover:bg-red-50'}
        >
          Échecs uniquement
        </Button>
        <Button variant="outline" size="sm" onClick={load} data-testid="access-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
        <div className="ml-auto flex gap-3 text-xs">
          <span><Check className="w-3 h-3 inline text-green-600" /> {successCount} ouvertures</span>
          <span><X className="w-3 h-3 inline text-red-600" /> {failCount} refus</span>
        </div>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F7] text-[#737373]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date / Heure</th>
                <th className="text-left px-3 py-2 font-medium">Statut</th>
                <th className="text-left px-3 py-2 font-medium">Code</th>
                <th className="text-left px-3 py-2 font-medium">Espace</th>
                <th className="text-left px-3 py-2 font-medium">Utilisateur</th>
                <th className="text-left px-3 py-2 font-medium">Device</th>
                <th className="text-left px-3 py-2 font-medium">IP</th>
                <th className="px-3 py-2 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-8 text-[#737373]">Chargement…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-[#737373]">Aucune tentative enregistrée</td></tr>
              ) : logs.map((l, idx) => (
                <tr key={l.id || idx} className="border-t border-[#E2E2DF]" data-testid={`access-log-row-${idx}`}>
                  <td className="px-3 py-2 font-mono text-xs text-[#1A3C34]">
                    {(l.timestamp || '').slice(0, 19).replace('T', ' ')}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className={`text-xs ${reasonClass[l.reason] || 'bg-gray-100 text-gray-700'}`}>
                      {l.access ? '✓ ' : '✗ '}{reasonLabel[l.reason] || l.reason}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-mono text-[#1A3C34]">{l.code_attempted}</td>
                  <td className="px-3 py-2 text-[#1A3C34]">{l.space_name || '—'}</td>
                  <td className="px-3 py-2 text-[#737373] text-xs">{l.user_email || '—'}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{l.device || '—'}</Badge></td>
                  <td className="px-3 py-2 text-[#737373] text-xs font-mono">{l.ip || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => deleteLog(l.id)}
                      disabled={!l.id || deletingId === l.id}
                      data-testid={`access-log-delete-${idx}`}
                      title="Supprimer cette entrée"
                      className="text-[#737373] hover:text-red-600 transition-colors p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// ADMIN KIOSK ADS MANAGER
// ============================================================
function AdminKioskAdsManager({ getHeaders }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = { title: '', image_url: '', media_type: '', duration_ms: 6000, order: 0, active: true };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  // Detect whether the URL points to a video (matches the backend heuristic)
  const isVideoUrl = (u) => /\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(String(u || '')) || /^data:video\//i.test(String(u || ''));

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 30 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload-kiosk-media`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setForm((f) => ({
        ...f,
        image_url: res.data.media_url || res.data.image_url,
        media_type: res.data.media_type || '',
      }));
      toast.success(`✓ ${res.data.media_type === 'video' ? 'Vidéo' : 'Image'} uploadée`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };
  const stopDrag = (e) => { e.preventDefault(); e.stopPropagation(); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/kiosk-ads`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      setAds(res.data || []);
    } catch (e) {
      toast.error('Erreur chargement publicités');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim() || !form.image_url.trim()) {
      toast.error('Titre et URL image requis');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/kiosk-ads/${editingId}`, form, {
          headers: getHeaders(),
          withCredentials: true,
        });
        toast.success('Publicité mise à jour');
      } else {
        await axios.post(`${API}/admin/kiosk-ads`, form, {
          headers: getHeaders(),
          withCredentials: true,
        });
        toast.success('Publicité ajoutée');
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    }
  };

  const edit = (ad) => {
    setEditingId(ad.ad_id);
    setForm({
      title: ad.title,
      image_url: ad.image_url,
      duration_ms: ad.duration_ms,
      order: ad.order,
      active: ad.active,
    });
  };

  const remove = async (ad_id) => {
    if (!window.confirm('Supprimer cette publicité ?')) return;
    try {
      await axios.delete(`${API}/admin/kiosk-ads/${ad_id}`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      toast.success('Publicité supprimée');
      load();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const toggleActive = async (ad) => {
    try {
      await axios.put(`${API}/admin/kiosk-ads/${ad.ad_id}`, { ...ad, active: !ad.active }, {
        headers: getHeaders(),
        withCredentials: true,
      });
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const pushNow = async () => {
    setPushing(true);
    try {
      const res = await axios.post(`${API}/admin/kiosk-ads/push-now`, {}, {
        headers: getHeaders(),
        withCredentials: true,
      });
      const n = res.data?.pushed_to ?? 0;
      if (n > 0) toast.success(`✓ Push envoyé sur ${n} Raspberry — visible dans quelques secondes`);
      else toast.warning('Aucun Raspberry actif trouvé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur push');
    } finally {
      setPushing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-kiosk-content">
      <div className="bg-[#FFF8F0] border border-[#C77D63]/30 rounded-md p-4 text-sm text-[#1A3C34]">
        <div className="flex items-start gap-2">
          <Layout className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Publicités sur l'écran Kiosk (entrée)</p>
            <p className="text-xs text-[#737373] mt-1">
              Ces médias défilent en plein écran sur l'écran de l'entrée pendant les temps morts. Pendant qu'un client tape son code, l'écran bascule automatiquement sur les instructions d'accès.
              <br />
              <strong>Images</strong> (.jpg .png .webp) ET <strong>vidéos</strong> (.mp4 .webm .mov) sont acceptées — la durée d'affichage s'applique aussi aux vidéos (mises en boucle).
              Toute publicité ajoutée, modifiée ou supprimée est <strong>poussée automatiquement</strong> sur les Raspberry actifs (en moins de 5 s). Sinon, utilisez le bouton « Pousser maintenant ».
            </p>
          </div>
        </div>
      </div>

      {/* Push-now banner */}
      <div className="flex items-center justify-between gap-3 bg-[#1A3C34]/5 border border-[#1A3C34]/20 rounded-md px-4 py-3">
        <div className="text-xs text-[#1A3C34]">
          <span className="font-semibold">Forcer la mise à jour des kiosks</span><br />
          Pousse immédiatement la liste actuelle de publicités sur tous les Raspberry actifs.
        </div>
        <Button
          data-testid="kiosk-push-now-btn"
          onClick={pushNow}
          disabled={pushing}
          className="h-9 bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 text-xs whitespace-nowrap"
        >
          {pushing ? '⏳ Envoi…' : '🚀 Pousser maintenant'}
        </Button>
      </div>

      {/* Add/Edit form */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3">
        <h3 className="font-medium text-[#1A3C34]">{editingId ? 'Modifier la publicité' : 'Ajouter une publicité'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#737373] mb-1">Titre *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Offre printemps -10%"
              className="h-10 border-[#E2E2DF] rounded-md"
              data-testid="kiosk-form-title"
            />
          </div>
          <div>
            <label className="block text-xs text-[#737373] mb-1">Média (image ou vidéo) *</label>
            <div
              onDrop={onDrop}
              onDragOver={stopDrag}
              onDragEnter={stopDrag}
              onClick={() => !uploading && fileRef.current?.click()}
              data-testid="kiosk-upload-dropzone"
              className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors mb-2 ${
                uploading ? 'border-[#1A3C34] bg-[#1A3C34]/5' : 'border-[#E2E2DF] hover:border-[#1A3C34] hover:bg-[#F0EFEA]'
              }`}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="kiosk-upload-input"
              />
              {uploading ? (
                <p className="text-sm text-[#1A3C34] font-medium">⏳ Upload en cours…</p>
              ) : (
                <>
                  <p className="text-sm text-[#1A3C34] font-medium">
                    Glissez-déposez ou cliquez pour choisir
                  </p>
                  <p className="text-[10px] text-[#737373] mt-1">
                    .jpg .png .webp .mp4 .webm .mov — max 30 MB
                  </p>
                </>
              )}
            </div>
            <label className="block text-[10px] text-[#737373] mb-1">…ou collez une URL externe</label>
            <Input
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value, media_type: '' })}
              placeholder="https://… (.jpg, .png, .webp, .mp4, .webm)"
              className="h-10 border-[#E2E2DF] rounded-md font-mono text-xs"
              data-testid="kiosk-form-url"
            />
          </div>
          <div>
            <label className="block text-xs text-[#737373] mb-1">Durée d'affichage (ms)</label>
            <Input
              type="number"
              value={form.duration_ms}
              onChange={(e) => setForm({ ...form, duration_ms: parseInt(e.target.value) || 6000 })}
              min="1500"
              max="60000"
              step="500"
              className="h-10 border-[#E2E2DF] rounded-md"
              data-testid="kiosk-form-duration"
            />
            <p className="text-[11px] text-[#737373] mt-1">Min 1500 ms — Max 60 000 ms (1 min)</p>
          </div>
          <div>
            <label className="block text-xs text-[#737373] mb-1">Ordre d'affichage</label>
            <Input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
              className="h-10 border-[#E2E2DF] rounded-md"
              data-testid="kiosk-form-order"
            />
          </div>
        </div>
        {form.image_url && (
          <div className="border border-dashed border-[#E2E2DF] rounded-md p-2 bg-[#F0EFEA]">
            <p className="text-xs text-[#737373] mb-2">Aperçu :</p>
            {isVideoUrl(form.image_url) ? (
              <video
                key={form.image_url}
                src={form.image_url}
                className="w-full max-h-64 mx-auto rounded bg-black"
                controls
                muted
                playsInline
                data-testid="kiosk-form-preview-video"
              />
            ) : (
              <img
                src={form.image_url}
                alt="preview"
                className="w-full max-h-64 mx-auto object-contain rounded bg-black"
                data-testid="kiosk-form-preview"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          {editingId && (
            <Button variant="outline" onClick={() => { setEditingId(null); setForm(emptyForm); }} data-testid="kiosk-form-cancel">
              Annuler
            </Button>
          )}
          <Button onClick={save} className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90" data-testid="kiosk-form-save">
            <Save className="w-4 h-4 mr-1" /> {editingId ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-[#1A3C34]">Publicités actuelles ({ads.length})</h3>
        {loading ? (
          <div className="text-center py-6 text-[#737373]">Chargement…</div>
        ) : ads.length === 0 ? (
          <div className="text-center py-8 text-[#737373] border border-dashed border-[#E2E2DF] rounded-md">
            Aucune publicité. Le Kiosk affiche l'écran d'accueil par défaut.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ads.map((ad) => {
              const isVideo = (ad.media_type === 'video') || isVideoUrl(ad.image_url);
              return (
              <div
                key={ad.ad_id}
                className={`bg-white border rounded-md p-3 ${ad.active ? 'border-[#E2E2DF]' : 'border-[#E2E2DF] opacity-50'}`}
                data-testid={`kiosk-ad-${ad.ad_id}`}
              >
                {/* Full-width preview — never cropped */}
                <div className="relative bg-black rounded-md overflow-hidden mb-3 aspect-video flex items-center justify-center">
                  {isVideo ? (
                    <video
                      src={ad.image_url}
                      className="w-full h-full object-contain"
                      muted
                      playsInline
                      preload="metadata"
                      onMouseEnter={(e) => e.currentTarget.play().catch(()=>{})}
                      onMouseLeave={(e) => { try { e.currentTarget.pause(); e.currentTarget.currentTime = 0; } catch(_){} }}
                    />
                  ) : (
                    <img
                      src={ad.image_url}
                      alt={ad.title}
                      className="w-full h-full object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  {isVideo && (
                    <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-mono px-2 py-0.5 rounded">VIDÉO</span>
                  )}
                  <span className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-mono px-2 py-0.5 rounded">#{ad.order}</span>
                </div>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h4 className="font-medium text-[#1A3C34] truncate flex-1">{ad.title}</h4>
                  <Badge variant="outline" className="text-[10px] flex-shrink-0">{ad.duration_ms} ms</Badge>
                </div>
                <p className="text-[10px] text-[#737373] truncate font-mono mb-2">{ad.image_url}</p>
                <div className="flex gap-1 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => edit(ad)} className="h-7 text-xs" data-testid={`kiosk-edit-${ad.ad_id}`}>
                    <Edit3 className="w-3 h-3 mr-1" /> Éditer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(ad)} className="h-7 text-xs" data-testid={`kiosk-toggle-${ad.ad_id}`}>
                    {ad.active ? <><X className="w-3 h-3 mr-1" /> Désactiver</> : <><Check className="w-3 h-3 mr-1" /> Activer</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(ad.ad_id)} className="h-7 text-xs text-red-600 hover:text-red-700 border-red-200" data-testid={`kiosk-del-${ad.ad_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================
// ADMIN DOOR DEVICES MANAGER (multi-Raspberry + remote open)
// ============================================================
function ClearFactsSettingsCard({ getHeaders }) {
  const [enabled, setEnabled] = useState(false);
  const [salesEmail, setSalesEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [originalEnabled, setOriginalEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/site-settings`, {
        headers: getHeaders(), withCredentials: true,
      });
      const cf = res.data?.clearfacts || {};
      const en = Boolean(cf.enabled);
      const em = String(cf.sales_email || '');
      setEnabled(en); setOriginalEnabled(en);
      setSalesEmail(em); setOriginalEmail(em);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const dirty = enabled !== originalEnabled || salesEmail.trim() !== originalEmail.trim();

  const save = async () => {
    const trimmed = salesEmail.trim();
    if (enabled && (!trimmed || !trimmed.includes('@'))) {
      toast.error('Email ClearFacts invalide');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        clearfacts: { enabled, sales_email: trimmed },
      }, { headers: getHeaders(), withCredentials: true });
      setOriginalEnabled(enabled); setOriginalEmail(trimmed);
      toast.success('Configuration ClearFacts enregistrée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (dirty) { toast.error('Enregistrez d\'abord avant de tester'); return; }
    if (!enabled || !salesEmail) { toast.error('Activez et renseignez un email d\'abord'); return; }
    setTesting(true);
    try {
      await axios.post(`${API}/admin/clearfacts/test`, {}, { headers: getHeaders(), withCredentials: true });
      toast.success(`Email de test envoyé à ${salesEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du test');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-lg p-5 mb-4" data-testid="clearfacts-settings-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#1A3C34]/8 flex items-center justify-center flex-shrink-0">
          <span className="text-xl">📒</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-base font-semibold text-[#1A3C34]">
              Comptabilité — Auto-forward ClearFacts (Kyte)
            </h3>
            {!loading && originalEnabled && originalEmail && (
              <Badge data-testid="clearfacts-status-active" className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                ● actif
              </Badge>
            )}
            {!loading && (!originalEnabled || !originalEmail) && (
              <Badge data-testid="clearfacts-status-inactive" className="text-[10px] bg-orange-100 text-orange-700 hover:bg-orange-100">
                ○ inactif
              </Badge>
            )}
          </div>
          <p className="text-xs text-[#737373] mt-1 leading-relaxed">
            Quand activé, chaque facture (vente) générée est automatiquement envoyée en copie à votre comptable
            via votre adresse <strong>Sales Book ClearFacts</strong> (kyte.clearfacts.be → Settings → Email
            addresses). Plus besoin de réimporter — tout part dans Kyte/Peppol automatiquement.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6 text-sm text-[#737373]">Chargement…</div>
      ) : (
        <>
          <div className="space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#1A3C34]"
                data-testid="clearfacts-enabled-toggle"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-[#1A3C34]">
                  Activer l'envoi automatique de chaque facture à ClearFacts
                </span>
                <p className="text-[11px] text-[#737373] mt-0.5">
                  S'applique à toutes les factures (B2B + B2C) dès leur génération.
                </p>
              </div>
            </label>

            <div>
              <label className="block text-xs font-medium text-[#1A3C34] mb-1">
                Email ClearFacts (Sales Book)
              </label>
              <Input
                type="email"
                value={salesEmail}
                onChange={(e) => setSalesEmail(e.target.value)}
                placeholder="salesbook-XXXXXX@clearfacts.be"
                className="h-9 text-sm font-mono"
                disabled={!enabled}
                data-testid="clearfacts-sales-email-input"
              />
              <p className="text-[11px] text-[#737373] mt-1">
                Demandez à Fiducial votre adresse "Sales Book" personnelle. Format type : <code className="bg-[#F9F9F7] px-1 rounded">salesbook-xxxx@clearfacts.be</code>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#E2E2DF]">
            <Button
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="clearfacts-save-btn"
              className="h-8 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white text-xs"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={sendTest}
              disabled={dirty || testing || !originalEnabled || !originalEmail}
              data-testid="clearfacts-test-btn"
              className="h-8 text-xs border-[#E2E2DF]"
              title={dirty ? "Enregistrez d'abord" : "Envoyer un mail de test à ClearFacts"}
            >
              {testing ? 'Envoi…' : '✉ Envoyer un test'}
            </Button>
            {dirty && (
              <span className="text-[11px] text-orange-700 ml-auto">
                Modifications non enregistrées
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function HoldOpenBookingsBanner({ getHeaders }) {
  const [items, setItems] = useState([]);
  const [today, setToday] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/door-devices/hold-open-bookings`,
        { headers: getHeaders(), withCredentials: true },
      );
      setItems(res.data?.items || []);
      setToday(res.data?.today || '');
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (items.length === 0) {
    return (
      <div className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-md p-3 text-xs text-[#737373] flex items-center gap-2" data-testid="hold-open-bookings-empty">
        <Unlock className="w-3.5 h-3.5" />
        Aucune réservation en mode "porte ouverte" aujourd'hui ({today}).
      </div>
    );
  }
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="hold-open-bookings-banner">
      <div className="flex items-center gap-2 mb-2">
        <Unlock className="w-4 h-4 text-amber-700" />
        <p className="font-medium text-amber-900 text-sm">
          Mode "porte ouverte" — {items.length} séance{items.length > 1 ? 's' : ''} aujourd'hui ({today})
        </p>
        <button onClick={load} className="ml-auto text-[11px] text-amber-700 hover:underline" data-testid="hold-open-refresh">↻</button>
      </div>
      <div className="space-y-1">
        {items.map((b) => (
          <div
            key={b.booking_id}
            className="bg-white border border-amber-100 rounded px-2 py-1.5 text-xs flex items-center gap-3"
            data-testid={`hold-open-row-${b.booking_id}`}
          >
            <span className="font-mono font-semibold text-[#1A3C34]">{b.start_time}–{b.end_time}</span>
            <span className="text-[#1A3C34]">{b.space_name}</span>
            <span className="text-[#737373] flex-1 min-w-0 truncate">
              {b.user_name || '—'} <span className="text-[#C2C2BD]">({b.user_email})</span>
            </span>
            {b.is_recurring && <span className="text-[10px] bg-[#C77D63]/15 text-[#C77D63] px-1.5 py-0.5 rounded">série</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


function SmsGatewayTester({ getHeaders }) {
  const [status, setStatus] = useState(null); // {configured, health, stats_24h, last_log}
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState("Test Flexi-location ✓");
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [retrying, setRetrying] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/sms/status`, { headers: getHeaders(), withCredentials: true });
      setStatus(res.data || null);
    } catch {
      setStatus({ configured: false, health: { reachable: false, ok: false }, stats_24h: { sent: 0, failed: 0 } });
    }
  }, [getHeaders]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const res = await axios.get(`${API}/admin/sms/logs?limit=50${params}`, { headers: getHeaders(), withCredentials: true });
      setLogs(res.data?.items || []);
    } catch {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }, [getHeaders, statusFilter]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (expanded) loadLogs(); }, [expanded, loadLogs]);

  const sendTest = async () => {
    if (!phone.trim()) {
      toast.error('Numéro requis (ex : +32470123456)');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/sms/test`, { phone: phone.trim(), message: message.trim() }, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success('SMS envoyé via la passerelle ✓');
      loadStatus();
      if (expanded) loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec envoi');
      loadStatus();
      if (expanded) loadLogs();
    } finally {
      setSending(false);
    }
  };

  const retry = async (logId) => {
    setRetrying(logId);
    try {
      await axios.post(`${API}/admin/sms/retry`, { log_id: logId }, { headers: getHeaders(), withCredentials: true });
      toast.success('SMS renvoyé ✓');
      loadStatus();
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du renvoi');
      loadLogs();
    } finally {
      setRetrying(null);
    }
  };

  const configured = status?.configured;
  const health = status?.health || {};
  const stats = status?.stats_24h || { sent: 0, failed: 0 };
  // "online" requires BOTH the cloud relay reachable AND the Android phone polling.
  // If the device info isn't provided yet (older backend), fall back to cloud-only.
  const phoneAlive = health?.device ? !!health.device.online : true;
  const online = configured && health.reachable && health.ok && phoneAlive;

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-md p-3" data-testid="sms-gateway-panel">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex-shrink-0">📱</span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="font-medium text-[#1A3C34] text-sm">Passerelle SMS (Android)</p>
            {configured === true && <Badge data-testid="sms-status-configured" className="text-[10px] bg-green-100 text-green-700 hover:bg-green-100">Configurée</Badge>}
            {configured === false && <Badge data-testid="sms-status-not-configured" className="text-[10px] bg-orange-100 text-orange-700 hover:bg-orange-100">Non configurée</Badge>}
            {configured === true && (
              online
                ? <Badge data-testid="sms-status-online" className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">● en ligne</Badge>
                : <Badge data-testid="sms-status-offline" className="text-[10px] bg-red-100 text-red-700 hover:bg-red-100">● hors ligne</Badge>
            )}
            {configured === true && (
              <span className="text-[11px] text-[#737373] ml-auto">
                24h : <strong className="text-emerald-700">{stats.sent} envoyés</strong> · <strong className="text-red-600">{stats.failed} échecs</strong>
              </span>
            )}
            <button
              type="button"
              onClick={loadStatus}
              className="text-[11px] text-[#737373] hover:text-[#1A3C34] underline"
              data-testid="sms-status-refresh"
            >
              ↻ rafraîchir
            </button>
          </div>

          {configured === false ? (
            <p className="text-xs text-[#737373] leading-relaxed">
              Pour activer l'envoi des codes par SMS, installez l'app <a href="https://sms-gate.app/getting-started/android-app/" target="_blank" rel="noreferrer" className="text-[#C77D63] underline">SMS Gateway</a> sur votre Android, activez le mode <strong>Cloud Server</strong>, puis ajoutez <code className="bg-white px-1 rounded">SMS_GATEWAY_USERNAME</code> et <code className="bg-white px-1 rounded">SMS_GATEWAY_PASSWORD</code> dans les variables d'environnement Render.
            </p>
          ) : (
            <>
              {!online && health.error && (
                <p className="text-xs text-red-700 mb-2 break-words" data-testid="sms-health-error">
                  ⚠ Passerelle injoignable : {health.error}
                </p>
              )}
              {/* Android phone status — most common failure mode is the phone going offline */}
              {health.device && (
                (() => {
                  const off = health.device.offline_seconds;
                  const isOnline = !!health.device.online;
                  let ago = '?';
                  if (typeof off === 'number') {
                    if (off < 60) ago = `${off}s`;
                    else if (off < 3600) ago = `${Math.round(off / 60)} min`;
                    else if (off < 86400) ago = `${Math.round(off / 3600)} h`;
                    else ago = `${Math.round(off / 86400)} j`;
                  }
                  return (
                    <div
                      data-testid="sms-device-status"
                      className={`text-xs rounded-md px-3 py-2 mb-3 border ${isOnline ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        <strong>Téléphone Android {isOnline ? 'en ligne' : 'HORS LIGNE'}</strong>
                        <span className="font-mono opacity-70">{health.device.name || health.device.id}</span>
                        <span className="opacity-70">·&nbsp;dernière activité&nbsp;: il y a {ago}</span>
                      </div>
                      {!isOnline && (
                        <p className="mt-1 text-[11px] leading-relaxed">
                          ⚠ Aucun SMS ne partira tant que le téléphone n'est pas revenu en ligne. Vérifiez sur place&nbsp;:
                          (1) l'app <em>SMS Gateway</em> est lancée (notification visible), (2) WiFi/4G actif,
                          (3) batterie pas en mode économie / Doze (Paramètres → Apps → SMS Gateway → Batterie&nbsp;: <strong>Sans restriction</strong>).
                          Les messages déjà envoyés sont conservés en attente et partiront automatiquement à la reconnexion.
                        </p>
                      )}
                    </div>
                  );
                })()
              )}
              <p className="text-xs text-[#737373] mb-2">Envoyer un SMS de test pour vérifier que la passerelle fonctionne. Le téléphone Android doit être allumé + connecté à internet.</p>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+32 470 12 34 56"
                  className="h-9 max-w-[220px] text-sm"
                  data-testid="sms-test-phone"
                />
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="h-9 flex-1 min-w-[200px] text-sm"
                  data-testid="sms-test-message"
                  maxLength={300}
                />
                <Button
                  size="sm"
                  onClick={sendTest}
                  disabled={sending || !phone.trim()}
                  data-testid="sms-test-send-btn"
                  className="h-9 bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white text-xs"
                >
                  {sending ? 'Envoi…' : 'Envoyer test'}
                </Button>
              </div>

              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="text-xs text-[#1A3C34] hover:underline"
                data-testid="sms-logs-toggle"
              >
                {expanded ? '▾ Masquer l\'historique' : '▸ Voir l\'historique des SMS'}
              </button>

              {expanded && (
                <div className="mt-2 bg-white border border-[#E2E2DF] rounded p-2" data-testid="sms-logs-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-[#737373]">Filtre :</span>
                    {['all', 'sent', 'failed', 'skipped'].map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`text-[11px] px-2 py-0.5 rounded border ${statusFilter === s ? 'bg-[#1A3C34] text-white border-[#1A3C34]' : 'bg-white text-[#737373] border-[#E2E2DF] hover:bg-[#F9F9F7]'}`}
                        data-testid={`sms-logs-filter-${s}`}
                      >
                        {s === 'all' ? 'Tous' : s === 'sent' ? 'Envoyés' : s === 'failed' ? 'Échecs' : 'Ignorés'}
                      </button>
                    ))}
                    <button
                      onClick={loadLogs}
                      className="text-[11px] text-[#737373] hover:text-[#1A3C34] underline ml-auto"
                      data-testid="sms-logs-refresh"
                    >
                      ↻
                    </button>
                  </div>

                  {loadingLogs ? (
                    <div className="text-center py-4 text-[#737373] text-xs">Chargement…</div>
                  ) : logs.length === 0 ? (
                    <div className="text-center py-4 text-[#737373] text-xs" data-testid="sms-logs-empty">Aucun SMS pour le moment.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="text-left text-[#737373] border-b border-[#E2E2DF]">
                            <th className="py-1 pr-2 font-medium">Quand</th>
                            <th className="py-1 pr-2 font-medium">Téléphone</th>
                            <th className="py-1 pr-2 font-medium">Type</th>
                            <th className="py-1 pr-2 font-medium">Statut</th>
                            <th className="py-1 pr-2 font-medium">Message</th>
                            <th className="py-1 pr-2 font-medium text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody data-testid="sms-logs-table">
                          {logs.map((l) => (
                            <tr key={l.log_id} className="border-b border-[#F0F0EE]" data-testid={`sms-log-row-${l.log_id}`}>
                              <td className="py-1 pr-2 whitespace-nowrap text-[#737373]">{fmtTime(l.created_at)}</td>
                              <td className="py-1 pr-2 font-mono">{l.phone || l.phone_raw || '—'}</td>
                              <td className="py-1 pr-2 text-[#737373]">{l.kind || '—'}</td>
                              <td className="py-1 pr-2">
                                {l.status === 'sent' && <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Envoyé</span>}
                                {l.status === 'failed' && <span className="inline-block px-1.5 py-0.5 rounded bg-red-100 text-red-700">Échec</span>}
                                {l.status === 'skipped' && <span className="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Ignoré</span>}
                              </td>
                              <td className="py-1 pr-2 max-w-[280px] truncate" title={l.message}>{l.message || '—'}</td>
                              <td className="py-1 pr-2 text-right">
                                <button
                                  onClick={() => retry(l.log_id)}
                                  disabled={retrying === l.log_id || !l.phone}
                                  data-testid={`sms-log-retry-${l.log_id}`}
                                  className="text-[11px] text-[#1A3C34] hover:underline disabled:opacity-40 disabled:no-underline"
                                  title={!l.phone ? 'Pas de téléphone' : 'Renvoyer ce SMS'}
                                >
                                  {retrying === l.log_id ? '…' : 'Renvoyer'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {logs.some((l) => l.status === 'failed' && l.error) && (
                        <details className="mt-2 text-[11px] text-[#737373]">
                          <summary className="cursor-pointer">Détails des erreurs</summary>
                          <ul className="mt-1 space-y-1">
                            {logs.filter((l) => l.status === 'failed' && l.error).slice(0, 5).map((l) => (
                              <li key={`err-${l.log_id}`} className="bg-red-50 border border-red-100 rounded px-2 py-1">
                                <span className="font-mono">{l.phone || l.phone_raw}</span> — <span className="break-all">{l.error}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}



function UpdateRaspberryBanner() {
  const [copied, setCopied] = useState(false);
  const baseUrl = (() => {
    if (typeof window === 'undefined') return '';
    const o = window.location.origin;
    return o.includes('preview.emergentagent.com') ? o : (o.includes('flexi-location') ? 'https://flexi-location.be' : o);
  })();
  const oneLiner = `curl -fsSL ${baseUrl}/api/access/raspberry-update | bash`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(oneLiner);
      setCopied(true);
      toast.success('Commande copiée');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copie impossible — sélectionnez manuellement');
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm" data-testid="update-pi-banner">
      <div className="flex items-start gap-2">
        <Download className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-blue-900">Mettre à jour les Raspberry</p>
          <p className="text-xs text-blue-800/80 mt-1 leading-relaxed">
            Si un Pi répond <code className="bg-white px-1 rounded">unknown command type: wifi_add</code> dans le journal, il faut mettre à jour son code. Connectez-vous en SSH au Pi et lancez la commande ci-dessous. Aucune perte de configuration : seuls les fichiers <code className="bg-white px-1 rounded">.py</code> sont remplacés.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code
              data-testid="update-pi-cmd"
              className="flex-1 min-w-0 bg-white border border-blue-200 rounded px-2 py-1.5 text-xs font-mono text-[#1A3C34] truncate"
              title={oneLiner}
            >
              {oneLiner}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={copy}
              data-testid="copy-update-cmd-btn"
              className="border-blue-300 text-blue-700 hover:bg-blue-100 h-8 text-xs flex-shrink-0"
            >
              {copied ? <><Check className="w-3 h-3 mr-1" /> Copié</> : <><Copy className="w-3 h-3 mr-1" /> Copier</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}



function AdminLightsManager({ getHeaders }) {
  const [spaces, setSpaces] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sp, ss] = await Promise.all([
        axios.get(`${API}/admin/spaces`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/site-settings`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setSpaces(sp.data || []);
      const lg = ss.data?.lighting || {};
      const du = ss.data?.door_unlock || {};
      setCfg({
        pre_minutes: Number(lg.pre_minutes ?? 15),
        post_minutes: Number(lg.post_minutes ?? 15),
        relock_delay_seconds: Number(du.relock_delay_seconds ?? 2),
        max_hold_seconds: Number(du.max_hold_seconds ?? 300),
      });
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateSpaceLightPin = async (space_id, light_gpio_pin) => {
    try {
      await axios.put(
        `${API}/admin/spaces/${space_id}`,
        { light_gpio_pin: Number(light_gpio_pin) || 0 },
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success("Broche enregistrée");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    }
  };

  const saveTimings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, {
        lighting: {
          pre_minutes: Math.max(0, Math.min(120, cfg.pre_minutes || 0)),
          post_minutes: Math.max(0, Math.min(120, cfg.post_minutes || 0)),
        },
        door_unlock: {
          relock_delay_seconds: Math.max(0, Math.min(30, cfg.relock_delay_seconds || 0)),
          max_hold_seconds: Math.max(30, Math.min(3600, cfg.max_hold_seconds || 300)),
        },
      }, { headers: getHeaders(), withCredentials: true });
      toast.success('Configuration enregistrée — appliquée au prochain sync (≤ 30 min)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="text-sm text-[#737373]">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-lights-manager">
      {/* === Timings === */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <h3 className="text-sm font-semibold text-[#1A3C34] mb-1 flex items-center gap-2">
          <Lightbulb className="w-4 h-4" /> Délais d'allumage automatique
        </h3>
        <p className="text-xs text-[#737373] mb-4">
          Les lumières s'allument avant le début de la réservation et s'éteignent après la fin.
          Ces délais s'appliquent à toutes les pièces équipées d'un relais.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#1A3C34] mb-1">
              Allumage avant la réservation (min)
            </label>
            <Input
              type="number" min={0} max={120}
              value={cfg.pre_minutes}
              onChange={(e) => setCfg({ ...cfg, pre_minutes: Number(e.target.value || 0) })}
              className="h-9 text-sm"
              data-testid="lighting-pre-minutes"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1A3C34] mb-1">
              Extinction après la fin (min)
            </label>
            <Input
              type="number" min={0} max={120}
              value={cfg.post_minutes}
              onChange={(e) => setCfg({ ...cfg, post_minutes: Number(e.target.value || 0) })}
              className="h-9 text-sm"
              data-testid="lighting-post-minutes"
            />
          </div>
        </div>
      </div>

      {/* === Smart re-lock (linked feature, kept here so the admin sees both
            "timing" configs in one place) === */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <h3 className="text-sm font-semibold text-[#1A3C34] mb-1 flex items-center gap-2">
          <Lock className="w-4 h-4" /> Re-verrouillage intelligent (serrure électrique)
        </h3>
        <p className="text-xs text-[#737373] mb-4">
          Pour les serrures à émission (fail-secure), le relais reste actif tant que la porte est ouverte. Dès que le capteur Reed détecte la fermeture, on attend ce délai pour s'assurer que le pêne s'engage correctement, puis le verrou se ré-arme.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#1A3C34] mb-1">
              Délai après fermeture (s)
            </label>
            <Input
              type="number" min={0} max={30}
              value={cfg.relock_delay_seconds}
              onChange={(e) => setCfg({ ...cfg, relock_delay_seconds: Number(e.target.value || 0) })}
              className="h-9 text-sm"
              data-testid="relock-delay-seconds"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1A3C34] mb-1">
              Maintien max si porte reste ouverte (s)
            </label>
            <Input
              type="number" min={30} max={3600}
              value={cfg.max_hold_seconds}
              onChange={(e) => setCfg({ ...cfg, max_hold_seconds: Number(e.target.value || 300) })}
              className="h-9 text-sm"
              data-testid="max-hold-seconds"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={saveTimings}
          disabled={saving}
          className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 h-9 text-xs"
          data-testid="lights-save-timings"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer les délais'}
        </Button>
      </div>

      {/* === Per-space GPIO + tester === */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <h3 className="text-sm font-semibold text-[#1A3C34] mb-1">Lumières par espace</h3>
        <p className="text-xs text-[#737373] mb-4">
          Associez chaque espace à la broche GPIO du Raspberry qui pilote son relais (0 = aucune). Les broches déclarées ici doivent figurer dans <code className="bg-[#F0EFEA] px-1">light_pins</code> du <code className="bg-[#F0EFEA] px-1">config.json</code> du Pi.
        </p>
        <div className="space-y-3">
          {spaces.length === 0 ? (
            <p className="text-xs text-[#737373] italic">Aucun espace configuré.</p>
          ) : spaces.map((sp) => (
            <div
              key={sp.space_id}
              className="flex flex-wrap items-center gap-3 py-2 border-b border-[#F0EFEA] last:border-b-0"
              data-testid={`lights-row-${sp.space_id}`}
            >
              <span className="font-medium text-sm text-[#1A3C34] min-w-[160px]">{sp.name}</span>
              <span className="text-xs text-[#737373]">Broche BCM&nbsp;:</span>
              <Input
                type="number" min={0} max={40}
                defaultValue={sp.light_gpio_pin ?? 0}
                onBlur={(e) => {
                  const v = Number(e.target.value || 0);
                  if (v === (sp.light_gpio_pin ?? 0)) return;
                  updateSpaceLightPin(sp.space_id, v);
                }}
                className="w-20 h-7 text-xs px-2"
                data-testid={`lights-pin-${sp.space_id}`}
              />
              <SpaceLightTester
                API={API}
                getHeaders={getHeaders}
                space_id={sp.space_id}
                enabled={Boolean(sp.light_gpio_pin)}
              />
              {!sp.light_gpio_pin && (
                <span className="text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                  ⚠ Lumière non configurée
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* === Live diagnostic — what the Pi *should* be doing right now === */}
      <LightingDiagnostic API={API} getHeaders={getHeaders} />
    </div>
  );
}


function LightingDiagnostic({ API, getHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchDiag = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/lighting/diagnostic`, {
        headers: getHeaders(), withCredentials: true,
      });
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  useEffect(() => {
    fetchDiag();
    // Live refresh every 10s so the admin sees the exact state the Pi will compute.
    const id = setInterval(fetchDiag, 10000);
    return () => clearInterval(id);
  }, [fetchDiag]);

  if (loading && !data) return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-5 text-sm text-[#737373]" data-testid="lighting-diagnostic-loading">
      Chargement du diagnostic…
    </div>
  );

  if (err && !data) return (
    <div className="bg-white border border-red-200 rounded-md p-5 text-sm text-red-700" data-testid="lighting-diagnostic-error">
      {err}
    </div>
  );

  if (!data) return null;
  const nowStr = new Date(data.now).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-5" data-testid="lighting-diagnostic">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-[#1A3C34] flex items-center gap-2">
            <Lightbulb className="w-4 h-4" /> Diagnostic en direct
          </h3>
          <p className="text-xs text-[#737373] mt-1">
            État que le Raspberry devrait appliquer à cet instant (rafraîchi toutes les 10s).
            Pré-allumage <strong>{data.config.pre_minutes}min</strong> · extinction <strong>{data.config.post_minutes}min</strong> après · tick Pi <strong>{data.config.interval_seconds}s</strong>.
          </p>
        </div>
        <div className="text-[11px] text-[#737373] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
          Maintenant : {nowStr}
        </div>
      </div>

      {data.warnings && data.warnings.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="lighting-diagnostic-warnings">
          <p className="text-xs font-semibold text-amber-900 mb-1">⚠ Configuration incomplète</p>
          <ul className="text-xs text-amber-800 space-y-0.5 list-disc list-inside">
            {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        {data.spaces.map((sp) => {
          const isOn = sp.current_state === 'ON';
          return (
            <div
              key={sp.space_id}
              className={`flex items-center gap-3 p-3 rounded-md border ${isOn ? 'bg-amber-50 border-amber-300' : 'bg-[#F8F8F6] border-[#E2E2DF]'}`}
              data-testid={`diag-space-${sp.space_id}`}
            >
              {/* Bulb indicator */}
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isOn ? 'bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.6)]' : 'bg-[#E2E2DF]'}`}>
                <Lightbulb className={`w-4 h-4 ${isOn ? 'text-white' : 'text-[#737373]'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-[#1A3C34]">{sp.name}</span>
                  {sp.light_gpio_pin ? (
                    <span className="text-[10px] font-mono bg-white border border-[#E2E2DF] text-[#737373] px-1.5 py-0.5 rounded">
                      BCM {sp.light_gpio_pin}
                    </span>
                  ) : (
                    <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">
                      Aucune broche
                    </span>
                  )}
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isOn ? 'bg-amber-200 text-amber-900' : 'bg-[#E2E2DF] text-[#737373]'}`}>
                    {sp.current_state}
                  </span>
                </div>
                {sp.current_booking ? (
                  <p className="text-xs text-[#737373] mt-1">
                    Active maintenant : code <strong className="font-mono">{sp.current_booking.code}</strong>
                    {sp.current_booking.user_name && <> · {sp.current_booking.user_name}</>}
                    {' '}({sp.current_booking.start_time} → {sp.current_booking.end_time})
                  </p>
                ) : sp.bookings_today.length > 0 ? (
                  <p className="text-xs text-[#737373] mt-1">
                    {sp.bookings_today.length} réservation(s) prévue(s) aujourd'hui
                  </p>
                ) : (
                  <p className="text-xs text-[#9A9A9A] italic mt-1">Aucune réservation aujourd'hui</p>
                )}
                {sp.next_change && (
                  <p className="text-[11px] text-[#1A3C34] mt-1">
                    → Prochain changement : <strong>{sp.next_change.to}</strong> à <strong>{sp.next_change.at}</strong>
                    {sp.next_change.user_name && <> (code {sp.next_change.booking_code} — {sp.next_change.user_name})</>}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SpaceLightTester({ API, getHeaders, space_id, enabled }) {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const trigger = async (next) => {
    setBusy(true);
    try {
      // Safety: auto-OFF after 60 s when turning ON, so a forgotten test
      // never leaves a lamp running indefinitely.
      const body = { on: next };
      if (next) body.duration_s = 60;
      await axios.post(`${API}/admin/space/${space_id}/test-light`, body, {
        headers: getHeaders(), withCredentials: true,
      });
      setOn(next);
      toast.success(next ? 'Lumière ALLUMÉE (auto-off 60s)' : 'Lumière éteinte');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur — Raspberry hors-ligne ?');
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;
  return (
    <button
      type="button"
      onClick={() => trigger(!on)}
      disabled={busy}
      data-testid={`space-${space_id}-light-toggle`}
      className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-md border text-xs font-medium transition disabled:opacity-50 ${on ? 'bg-amber-100 border-amber-400 text-amber-900' : 'bg-white border-[#E2E2DF] text-[#737373] hover:bg-[#F0EFEA]'}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-amber-500 animate-pulse' : 'bg-[#C7C7C2]'}`}></span>
      {busy ? '…' : (on ? 'ON' : 'OFF')} (test)
    </button>
  );
}


function DoorSensorConfig({ API, getHeaders }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/site-settings`, {
          headers: getHeaders(), withCredentials: true,
        });
        const ds = res.data?.door_sensor || {};
        setCfg({
          enabled: !!ds.enabled,
          alert_start: ds.alert_start || '22:00',
          alert_end: ds.alert_end || '06:00',
          alert_phone: ds.alert_phone || '',
          debounce_minutes: ds.debounce_minutes ?? 10,
          cooldown_minutes: ds.cooldown_minutes ?? 60,
        });
      } catch (e) {
        setCfg({ enabled: false, alert_start: '22:00', alert_end: '06:00', alert_phone: '', debounce_minutes: 10, cooldown_minutes: 60 });
      }
    })();
  }, [API, getHeaders]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, { door_sensor: cfg }, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success('Configuration capteur porte enregistrée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className="border-t border-[#E2E2DF] pt-5 mt-5" data-testid="door-sensor-config">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-[#1A3C34]">🚪 Détection porte ouverte tardive (Reed)</h3>
          <p className="text-xs text-[#737373]">
            Le capteur magnétique (GPIO 27) signale au backend chaque ouverture/fermeture.
            Si la porte reste OUVERTE pendant la plage horaire ci-dessous, un SMS est envoyé.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            data-testid="door-sensor-enabled"
          />
          <span>{cfg.enabled ? 'Activé' : 'Désactivé'}</span>
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-[#1A3C34] mb-1">Plage horaire — début</label>
          <Input
            type="time"
            value={cfg.alert_start}
            onChange={(e) => setCfg({ ...cfg, alert_start: e.target.value })}
            className="h-8 text-xs"
            data-testid="door-sensor-alert-start"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A3C34] mb-1">Plage horaire — fin</label>
          <Input
            type="time"
            value={cfg.alert_end}
            onChange={(e) => setCfg({ ...cfg, alert_end: e.target.value })}
            className="h-8 text-xs"
            data-testid="door-sensor-alert-end"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A3C34] mb-1">N° SMS destinataire</label>
          <Input
            type="tel"
            value={cfg.alert_phone}
            onChange={(e) => setCfg({ ...cfg, alert_phone: e.target.value })}
            placeholder="+32499..."
            className="h-8 text-xs"
            data-testid="door-sensor-alert-phone"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A3C34] mb-1">Anti-rebond (min ouvert avant SMS)</label>
          <Input
            type="number"
            min={1}
            max={60}
            value={cfg.debounce_minutes}
            onChange={(e) => setCfg({ ...cfg, debounce_minutes: Number(e.target.value || 10) })}
            className="h-8 text-xs"
            data-testid="door-sensor-debounce"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#1A3C34] mb-1">Cooldown SMS (min)</label>
          <Input
            type="number"
            min={5}
            max={720}
            value={cfg.cooldown_minutes}
            onChange={(e) => setCfg({ ...cfg, cooldown_minutes: Number(e.target.value || 60) })}
            className="h-8 text-xs"
            data-testid="door-sensor-cooldown"
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={save}
            disabled={saving}
            className="w-full h-8 text-xs bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
            data-testid="door-sensor-save"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
      <p className="text-[10px] text-[#737373] mt-3 italic">
        ℹ Les plages chevauchant minuit (ex. 22:00 → 06:00) sont supportées. La vérification se fait toutes les 30 min côté serveur.
      </p>
    </div>
  );
}


function AdminDoorDevicesManager({ getHeaders }) {
  const [devices, setDevices] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null); // device_id being opened
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', description: '' });
  const [busy, setBusy] = useState(null); // device_id with a pending lock/wifi action
  const [wifiModal, setWifiModal] = useState(null); // device_id whose wifi modal is open
  const [wifiForm, setWifiForm] = useState({ ssid: '', password: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/door-devices`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/spaces`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setDevices(dRes.data || []);
      setSpaces(sRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const openDoor = async (device_id) => {
    const reason = window.prompt('Motif de l\'ouverture (optionnel — apparaîtra dans le journal) ?', '');
    if (reason === null) return; // cancelled
    setOpening(device_id);
    try {
      const res = await axios.post(
        `${API}/admin/door-devices/${device_id}/open`,
        { duration_s: 5, reason: reason || 'Ouverture admin' },
        { headers: getHeaders(), withCredentials: true, timeout: 20000 },
      );
      const status = res.data?.status;
      if (status === 'acked') {
        toast.success(`✓ Porte ouverte (${device_id})`);
      } else if (status === 'failed') {
        toast.error(`✗ Le Raspberry a refusé : ${res.data?.note || ''}`);
      } else {
        toast.warning(`⏳ Commande mise en file. ${res.data?.note || ''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'ouverture');
    } finally {
      setOpening(null);
    }
  };

  const saveDevice = async () => {
    try {
      await axios.put(`${API}/admin/door-devices/${editingId}`, editForm, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success('Appareil mis à jour');
      setEditingId(null);
      load();
    } catch (e) {
      toast.error('Erreur enregistrement');
    }
  };

  const deleteDevice = async (device_id) => {
    if (!window.confirm(`Supprimer l'appareil ${device_id} ?\nIl sera réenregistré automatiquement à sa prochaine connexion.`)) return;
    try {
      await axios.delete(`${API}/admin/door-devices/${device_id}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success('Appareil supprimé');
      load();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const updateSpaceDevices = async (space_id, access_devices) => {
    try {
      await axios.put(`${API}/admin/spaces/${space_id}`, { access_devices }, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success('Portes assignées à l\'espace');
      load();
    } catch (e) {
      toast.error('Erreur enregistrement');
    }
  };

  const updateSpaceLightPin = async (space_id, light_gpio_pin) => {
    try {
      await axios.put(
        `${API}/admin/spaces/${space_id}`,
        { light_gpio_pin: Number(light_gpio_pin) || 0 },
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success('Lumière liée à l\'espace');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    }
  };

  const lockOpen = async (device_id) => {
    if (!window.confirm("Maintenir la porte ouverte en permanence ?\n\n⚠ Vérifiez que votre gâche supporte une activation prolongée du relais (certaines gâches « fail-secure » peuvent chauffer). Vous pourrez la refermer à tout moment.")) return;
    setBusy(device_id);
    try {
      const res = await axios.post(
        `${API}/admin/door-devices/${device_id}/lock-open`,
        { reason: 'Maintien admin' },
        { headers: getHeaders(), withCredentials: true, timeout: 20000 },
      );
      if (res.data?.status === 'acked') {
        toast.success('🔓 Porte maintenue ouverte');
      } else {
        toast.warning(`⏳ ${res.data?.note || 'En attente du Raspberry'}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally {
      setBusy(null);
    }
  };

  const lockClose = async (device_id) => {
    setBusy(device_id);
    try {
      const res = await axios.post(
        `${API}/admin/door-devices/${device_id}/lock-close`,
        {},
        { headers: getHeaders(), withCredentials: true, timeout: 20000 },
      );
      if (res.data?.status === 'acked') {
        toast.success('🔒 Porte refermée');
      } else {
        toast.warning(`⏳ ${res.data?.note || 'En attente du Raspberry'}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally {
      setBusy(null);
    }
  };

  const submitWifi = async () => {
    const ssid = (wifiForm.ssid || '').trim();
    const password = (wifiForm.password || '').trim();
    if (!ssid || password.length < 8) {
      toast.error('SSID requis et mot de passe ≥ 8 caractères (WPA2)');
      return;
    }
    const device_id = wifiModal;
    setBusy(device_id);
    try {
      const res = await axios.post(
        `${API}/admin/door-devices/${device_id}/wifi`,
        { ssid, password },
        { headers: getHeaders(), withCredentials: true, timeout: 35000 },
      );
      if (res.data?.status === 'acked') {
        toast.success(`✓ ${res.data?.note || 'Wi-Fi ajouté'}`);
        setWifiModal(null);
        setWifiForm({ ssid: '', password: '' });
      } else if (res.data?.status === 'failed') {
        toast.error(`✗ ${res.data?.note || 'Le Pi a refusé'}`);
      } else {
        toast.warning(`⏳ ${res.data?.note || 'Pi hors ligne — la commande sera traitée à la reconnexion'}`);
        setWifiModal(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec ajout Wi-Fi');
    } finally {
      setBusy(null);
    }
  };

  const isOnline = (last_seen) => {
    if (!last_seen) return false;
    const diff = Date.now() - new Date(last_seen).getTime();
    return diff < 90 * 1000; // < 90s = online
  };

  return (
    <div className="space-y-5" data-testid="admin-doors-content">
      <div className="bg-[#FFF8F0] border border-[#C77D63]/30 rounded-md p-4 text-sm text-[#1A3C34]">
        <div className="flex items-start gap-2">
          <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Gestion des portes (Raspberry Pi)</p>
            <p className="text-xs text-[#737373] mt-1">
              Chaque Raspberry s'enregistre automatiquement la première fois qu'il appelle l'API. Vous pouvez ouvrir n'importe quelle porte à distance, et lier des portes à des espaces spécifiques (par défaut une porte est valide pour tous les espaces — pratique pour la porte d'entrée principale).
            </p>
          </div>
        </div>
      </div>

      {/* Pi Update banner */}
      <UpdateRaspberryBanner />

      {/* SMS Gateway test */}
      <SmsGatewayTester getHeaders={getHeaders} />

      {/* Today's hold-open bookings */}
      <HoldOpenBookingsBanner getHeaders={getHeaders} />

      {/* Devices list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-[#1A3C34]">Raspberry Pi enregistrés ({devices.length})</h3>
          <ProvisionDeviceButton getHeaders={getHeaders} onProvisioned={load} />
        </div>
        {loading ? (
          <div className="text-center py-6 text-[#737373]">Chargement…</div>
        ) : devices.length === 0 ? (
          <div className="text-center py-8 text-[#737373] border border-dashed border-[#E2E2DF] rounded-md">
            Aucun appareil enregistré. Le premier Raspberry qui appellera l'API apparaîtra ici automatiquement.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {devices.map((d) => {
              const online = isOnline(d.last_seen);
              const editing = editingId === d.device_id;
              return (
                <div key={d.device_id} className="bg-white border border-[#E2E2DF] rounded-md p-4" data-testid={`device-${d.device_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      {editing ? (
                        <Input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} className="h-8 text-sm" data-testid={`device-edit-label-${d.device_id}`} />
                      ) : (
                        <h4 className="font-medium text-[#1A3C34]">{d.label || d.device_id}</h4>
                      )}
                      <p className="text-xs text-[#737373] font-mono mt-0.5">{d.device_id}</p>
                    </div>
                    <div className={`flex items-center gap-1 text-xs ${online ? 'text-green-600' : 'text-[#737373]'}`}>
                      <span className={`w-2 h-2 rounded-full ${online ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></span>
                      {online ? 'En ligne' : 'Hors ligne'}
                    </div>
                  </div>
                  {editing ? (
                    <div className="space-y-2">
                      <Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description (ex: Bâtiment A, salle 12)" className="h-8 text-sm" data-testid={`device-edit-desc-${d.device_id}`} />
                      <div className="flex gap-1">
                        <Button size="sm" onClick={saveDevice} className="bg-[#1A3C34] text-white h-7 text-xs"><Save className="w-3 h-3 mr-1" /> Enregistrer</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-7 text-xs">Annuler</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {d.description && <p className="text-xs text-[#737373] mb-2">{d.description}</p>}
                      <p className="text-xs text-[#737373] mb-1">
                        {d.last_seen ? (
                          <>Vu : {new Date(d.last_seen).toLocaleString('fr-BE')}</>
                        ) : 'Jamais vu'}
                        {d.last_ip && <span className="ml-2 font-mono">• {d.last_ip}</span>}
                      </p>
                      {d.heartbeat && (
                        <div
                          data-testid={`device-heartbeat-${d.device_id}`}
                          className="bg-[#F9F9F7] border border-[#E2E2DF] rounded-md px-2.5 py-1.5 mb-3 text-[10.5px] text-[#1A3C34] flex flex-wrap items-center gap-x-2.5 gap-y-1"
                        >
                          {d.heartbeat.kiosk_alive === true && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              Kiosk OK
                            </span>
                          )}
                          {d.heartbeat.kiosk_alive === false && (
                            <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                              Kiosk HS
                            </span>
                          )}
                          {d.heartbeat.agent_version && (
                            <span className="font-mono text-[#737373]">v{d.heartbeat.agent_version}</span>
                          )}
                          {typeof d.heartbeat.uptime_s === 'number' && (
                            <span className="text-[#737373]">
                              ↑ {d.heartbeat.uptime_s < 60
                                ? `${d.heartbeat.uptime_s}s`
                                : d.heartbeat.uptime_s < 3600
                                ? `${Math.round(d.heartbeat.uptime_s / 60)}min`
                                : d.heartbeat.uptime_s < 86400
                                ? `${Math.round(d.heartbeat.uptime_s / 3600)}h`
                                : `${Math.round(d.heartbeat.uptime_s / 86400)}j`}
                            </span>
                          )}
                          {typeof d.heartbeat.cpu_temp_c === 'number' && (
                            <span className={d.heartbeat.cpu_temp_c >= 75 ? 'text-red-600 font-medium' : 'text-[#737373]'}>
                              {d.heartbeat.cpu_temp_c.toFixed(1)}°C
                            </span>
                          )}
                          {typeof d.heartbeat.free_disk_mb === 'number' && (
                            <span className={d.heartbeat.free_disk_mb < 500 ? 'text-red-600 font-medium' : 'text-[#737373]'}>
                              {d.heartbeat.free_disk_mb >= 1024
                                ? `${(d.heartbeat.free_disk_mb / 1024).toFixed(1)} Go libre`
                                : `${d.heartbeat.free_disk_mb} Mo libre`}
                            </span>
                          )}
                        </div>
                      )}
                      {d.door_state && (
                        <div
                          data-testid={`device-door-state-${d.device_id}`}
                          className={`mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10.5px] font-medium ${d.door_state.is_open ? 'bg-orange-100 text-orange-800' : 'bg-emerald-100 text-emerald-800'}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${d.door_state.is_open ? 'bg-orange-500' : 'bg-emerald-500'}`}></span>
                          Porte {d.door_state.is_open ? 'OUVERTE' : 'fermée'}
                          {d.door_state.updated_at && (
                            <span className="font-normal opacity-70 ml-1">
                              · {new Date(d.door_state.updated_at).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex gap-1 flex-wrap">
                        <Button
                          onClick={() => openDoor(d.device_id)}
                          disabled={opening === d.device_id || busy === d.device_id}
                          className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 h-8 text-xs"
                          data-testid={`door-open-${d.device_id}`}
                        >
                          <Unlock className="w-3 h-3 mr-1" />
                          {opening === d.device_id ? 'Ouverture…' : 'Ouvrir maintenant'}
                        </Button>
                        {d.lock_state === 'open' ? (
                          <Button
                            onClick={() => lockClose(d.device_id)}
                            disabled={busy === d.device_id}
                            className="bg-red-600 text-white hover:bg-red-700 h-8 text-xs"
                            data-testid={`door-lock-close-${d.device_id}`}
                            title="La porte est actuellement maintenue ouverte"
                          >
                            <Lock className="w-3 h-3 mr-1" />
                            {busy === d.device_id ? '…' : 'Fermer maintenant'}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => lockOpen(d.device_id)}
                            disabled={busy === d.device_id}
                            variant="outline"
                            className="h-8 text-xs border-[#C77D63] text-[#C77D63] hover:bg-[#C77D63]/10"
                            data-testid={`door-lock-open-${d.device_id}`}
                          >
                            <Unlock className="w-3 h-3 mr-1" />
                            {busy === d.device_id ? '…' : 'Maintenir ouvert'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setWifiModal(d.device_id); setWifiForm({ ssid: '', password: '' }); }}
                          disabled={busy === d.device_id}
                          className="h-8 text-xs"
                          data-testid={`device-wifi-${d.device_id}`}
                        >
                          <Wifi className="w-3 h-3 mr-1" /> Wi-Fi
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditingId(d.device_id); setEditForm({ label: d.label || '', description: d.description || '' }); }} className="h-8 text-xs" data-testid={`device-edit-${d.device_id}`}>
                          <Edit3 className="w-3 h-3 mr-1" /> Renommer
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteDevice(d.device_id)} className="h-8 text-xs text-red-600 hover:text-red-700 border-red-200" data-testid={`device-del-${d.device_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      {d.lock_state === 'open' && (
                        <p className="text-[11px] text-red-600 mt-2 inline-flex items-center gap-1" data-testid={`device-locked-badge-${d.device_id}`}>
                          <Unlock className="w-3 h-3" />
                          Porte maintenue ouverte
                          {d.lock_state_since && (
                            <span className="text-[#737373]"> depuis {new Date(d.lock_state_since).toLocaleString('fr-BE')}</span>
                          )}
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Spaces ↔ Devices assignment */}
      <div className="border-t border-[#E2E2DF] pt-5">
        <h3 className="text-sm font-medium text-[#1A3C34] mb-1">Quelle porte ouvre quel espace ?</h3>
        <p className="text-xs text-[#737373] mb-3">
          Cochez les portes qui doivent valider les codes d'accès pour chaque espace. Aucune coche = toutes les portes (par défaut, comportement de la porte d'entrée principale).
        </p>
        <div className="space-y-2">
          {spaces.map((sp) => {
            const linked = sp.access_devices || [];
            return (
              <div key={sp.space_id} className="bg-white border border-[#E2E2DF] rounded-md p-3" data-testid={`space-doors-${sp.space_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div>
                    <h4 className="font-medium text-[#1A3C34] text-sm">{sp.name}</h4>
                    <p className="text-xs text-[#737373]">
                      {linked.length === 0 ? 'Toutes les portes (par défaut)' : `${linked.length} porte(s) liée(s)`}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {devices.length === 0 ? (
                    <span className="text-xs text-[#737373] italic">Aucune porte enregistrée</span>
                  ) : devices.map((d) => {
                    const checked = linked.includes(d.device_id);
                    return (
                      <label
                        key={d.device_id}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-xs transition ${checked ? 'bg-[#1A3C34] text-white border-[#1A3C34]' : 'bg-white border-[#E2E2DF] hover:border-[#1A3C34]'}`}
                        data-testid={`space-${sp.space_id}-device-${d.device_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...linked, d.device_id]
                              : linked.filter((x) => x !== d.device_id);
                            updateSpaceDevices(sp.space_id, next);
                          }}
                          className="hidden"
                        />
                        {checked && <Check className="w-3 h-3" />}
                        {d.label || d.device_id}
                      </label>
                    );
                  })}
                </div>
                {/* Lighting GPIO pin per space — 0 disables */}
                <div className="mt-3 pt-3 border-t border-[#E2E2DF]/60 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#737373]">💡 Broche GPIO lumière (BCM, 0 = aucune)&nbsp;:</span>
                  <Input
                    type="number"
                    min={0}
                    max={40}
                    defaultValue={sp.light_gpio_pin ?? 0}
                    onBlur={(e) => {
                      const v = Number(e.target.value || 0);
                      if (v === (sp.light_gpio_pin ?? 0)) return;
                      updateSpaceLightPin(sp.space_id, v);
                    }}
                    className="w-20 h-7 text-xs px-2"
                    data-testid={`space-${sp.space_id}-light-pin`}
                  />
                  <SpaceLightTester
                    API={API}
                    getHeaders={getHeaders}
                    space_id={sp.space_id}
                    enabled={Boolean(sp.light_gpio_pin)}
                  />
                  <span className="text-[#737373] italic">
                    Allumage 15 min avant, extinction 15 min après la réservation.
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Door-open sensor (reed relay) admin config */}
      <DoorSensorConfig API={API} getHeaders={getHeaders} />

      {/* WiFi modal */}
      {wifiModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setWifiModal(null); }}
          data-testid="wifi-modal-overlay"
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-sm w-full p-5" data-testid="wifi-modal">
            <div className="flex items-center gap-2 mb-3">
              <Wifi className="w-5 h-5 text-[#1A3C34]" />
              <h3 className="font-heading text-lg font-bold text-[#1A3C34]">Ajouter un Wi-Fi</h3>
            </div>
            <p className="text-xs text-[#737373] mb-4 leading-relaxed">
              Le nouveau réseau sera <strong>ajouté</strong> à la liste connue du Raspberry (l'actuel reste actif). Si le mot de passe est incorrect, le Pi reste joignable sur le réseau actuel.
            </p>
            <label className="block text-xs font-medium text-[#1A3C34] mb-1">SSID (nom du réseau)</label>
            <Input
              autoFocus
              value={wifiForm.ssid}
              onChange={(e) => setWifiForm({ ...wifiForm, ssid: e.target.value })}
              placeholder="ex: MaBox-2.4G"
              maxLength={32}
              className="mb-3"
              data-testid="wifi-modal-ssid"
            />
            <label className="block text-xs font-medium text-[#1A3C34] mb-1">Mot de passe (WPA2, ≥ 8 car.)</label>
            <Input
              type="password"
              value={wifiForm.password}
              onChange={(e) => setWifiForm({ ...wifiForm, password: e.target.value })}
              maxLength={63}
              minLength={8}
              className="mb-4"
              data-testid="wifi-modal-password"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setWifiModal(null)}
                disabled={busy === wifiModal}
                data-testid="wifi-modal-cancel"
              >
                Annuler
              </Button>
              <Button
                onClick={submitWifi}
                disabled={busy === wifiModal}
                className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90"
                data-testid="wifi-modal-submit"
              >
                {busy === wifiModal ? 'Envoi…' : 'Envoyer au Raspberry'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// ADMIN DEDICATED TENANTS MANAGER ("Bureaux dédiés")
// ============================================================
function AdminTenantsManager({ getHeaders }) {
  const [tenants, setTenants] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = {
    user_email: '', user_name: '', space_id: '',
    monthly_price: 350, start_date: new Date().toISOString().slice(0, 10),
    end_date: '', hours_start: '08:00', hours_end: '22:00', notes: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/tenants`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/spaces`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setTenants(tRes.data || []);
      setSpaces(sRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.user_email || !form.space_id || !form.monthly_price) {
      toast.error('Email, espace et prix requis');
      return;
    }
    try {
      const payload = { ...form, end_date: form.end_date || null };
      await axios.post(`${API}/admin/tenants`, payload, { headers: getHeaders(), withCredentials: true });
      toast.success('Bureau dédié créé');
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    }
  };

  const updateStatus = async (tenant_id, status) => {
    try {
      await axios.put(`${API}/admin/tenants/${tenant_id}`, { status }, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(status === 'ended' ? 'Contrat clôturé' : 'Contrat réactivé');
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const remove = async (tenant_id) => {
    if (!window.confirm('Supprimer définitivement ce contrat ?\n(Les factures déjà émises sont conservées.)')) return;
    try {
      await axios.delete(`${API}/admin/tenants/${tenant_id}`, { headers: getHeaders(), withCredentials: true });
      toast.success('Supprimé');
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const generateInvoice = async (tenant_id) => {
    const period = window.prompt('Pour quelle période ? (ex: "Mai 2026")', new Date().toLocaleDateString('fr-BE', { month: 'long', year: 'numeric' }));
    if (!period) return;
    try {
      const res = await axios.post(`${API}/admin/tenants/${tenant_id}/generate-invoice`, { period_label: period }, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success(`Facture ${res.data.invoice_number} générée et envoyée par email (${res.data.totals.tvac.toFixed(2)} € TVAC)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur génération');
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-tenants-content">
      <div className="bg-[#FFF8F0] border border-[#C77D63]/30 rounded-md p-4 text-sm text-[#1A3C34]">
        <div className="flex items-start gap-2">
          <Building className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Bureaux dédiés (location longue durée)</p>
            <p className="text-xs text-[#737373] mt-1">
              Chaque locataire reçoit un <strong>code d'accès permanent</strong> qui ouvre uniquement son espace privatif, dans une plage horaire définie. Vous générez sa facture mensuelle en un clic. Idéal pour des coachs, freelances ou indépendants qui veulent un bureau à eux du lundi au vendredi.
            </p>
          </div>
        </div>
      </div>

      <Button onClick={() => setShowForm(!showForm)} className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90" data-testid="tenant-toggle-form">
        {showForm ? <><X className="w-4 h-4 mr-1" /> Annuler</> : <><Building className="w-4 h-4 mr-1" /> Nouveau bureau dédié</>}
      </Button>

      {showForm && (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3" data-testid="tenant-create-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#737373] mb-1">Email du locataire *</label>
              <Input value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} placeholder="jean@example.com" className="h-10" data-testid="tenant-form-email" />
              <p className="text-[11px] text-[#737373] mt-1">Si l'utilisateur n'existe pas, il sera créé automatiquement.</p>
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">Nom (optionnel)</label>
              <Input value={form.user_name} onChange={(e) => setForm({ ...form, user_name: e.target.value })} placeholder="Jean Dupont" className="h-10" data-testid="tenant-form-name" />
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">Espace *</label>
              <Select value={form.space_id} onValueChange={(v) => setForm({ ...form, space_id: v })}>
                <SelectTrigger className="h-10 border-[#E2E2DF]" data-testid="tenant-form-space"><SelectValue placeholder="Choisir un espace" /></SelectTrigger>
                <SelectContent>
                  {spaces.map((s) => <SelectItem key={s.space_id} value={s.space_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">Loyer mensuel TVAC (€) *</label>
              <Input type="number" step="10" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: parseFloat(e.target.value) || 0 })} className="h-10" data-testid="tenant-form-price" />
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">Date de début *</label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-10" data-testid="tenant-form-start" />
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">Date de fin (vide = sans limite)</label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-10" data-testid="tenant-form-end" />
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">Heure d'accès — début</label>
              <Input type="time" value={form.hours_start} onChange={(e) => setForm({ ...form, hours_start: e.target.value })} className="h-10" data-testid="tenant-form-hours-start" />
            </div>
            <div>
              <label className="block text-xs text-[#737373] mb-1">Heure d'accès — fin</label>
              <Input type="time" value={form.hours_end} onChange={(e) => setForm({ ...form, hours_end: e.target.value })} className="h-10" data-testid="tenant-form-hours-end" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#737373] mb-1">Notes internes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ex: paiement par virement le 1er du mois" className="h-10" data-testid="tenant-form-notes" />
          </div>
          <div className="flex justify-end">
            <Button onClick={create} className="bg-[#1A3C34] text-white" data-testid="tenant-form-create">
              <Save className="w-4 h-4 mr-1" /> Créer le contrat
            </Button>
          </div>
          <p className="text-xs text-[#737373]">
            Un code d'accès à 6 chiffres sera généré automatiquement. Envoyez-le ensuite au client par message.
          </p>
        </div>
      )}

      {/* List */}
      <div>
        <h3 className="text-sm font-medium text-[#1A3C34] mb-2">Contrats actifs ({tenants.filter((t) => t.status === 'active').length})</h3>
        {loading ? (
          <div className="text-center py-6 text-[#737373]">Chargement…</div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-8 text-[#737373] border border-dashed border-[#E2E2DF] rounded-md">
            Aucun contrat. Cliquez « Nouveau bureau dédié » pour en créer un.
          </div>
        ) : (
          <div className="space-y-2">
            {tenants.map((t) => (
              <div key={t.tenant_id} className={`bg-white border rounded-md p-4 ${t.status === 'active' ? 'border-[#E2E2DF]' : 'border-[#E2E2DF] opacity-60'}`} data-testid={`tenant-${t.tenant_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-[250px]">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-[#1A3C34]">{t.user_name || t.user_email}</h4>
                      <Badge className={`text-xs ${t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {t.status === 'active' ? 'Actif' : 'Clôturé'}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#1A3C34]">📍 {t.space_name} · 💶 {t.monthly_price} €/mois TVAC</p>
                    <p className="text-xs text-[#737373] mt-1">
                      📧 {t.user_email}{t.user_phone && ` • 📱 ${t.user_phone}`}
                    </p>
                    <p className="text-xs text-[#737373]">
                      🕐 {t.hours_start}–{t.hours_end} · 📅 du {t.start_date}{t.end_date ? ` au ${t.end_date}` : ' (sans limite)'}
                    </p>
                    <p className="text-xs text-[#737373] mt-1">
                      🔑 Code d'accès : <code className="bg-[#F2EDE6] px-2 py-0.5 rounded font-mono text-[#1A3C34]">{t.access_code}</code>
                    </p>
                    {t.last_invoice && (
                      <p className="text-xs text-green-700 mt-1">
                        Dernière facture : {t.last_invoice.invoice_number} ({t.last_invoice.issued_at_display})
                      </p>
                    )}
                    {t.notes && <p className="text-xs text-[#737373] mt-1 italic">{t.notes}</p>}
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" onClick={() => generateInvoice(t.tenant_id)} className="bg-[#C77D63] text-white hover:bg-[#C77D63]/90 h-8 text-xs" data-testid={`tenant-invoice-${t.tenant_id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Facture du mois
                    </Button>
                    {t.status === 'active' ? (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(t.tenant_id, 'ended')} className="h-8 text-xs" data-testid={`tenant-end-${t.tenant_id}`}>
                        Clôturer
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(t.tenant_id, 'active')} className="h-8 text-xs" data-testid={`tenant-reactivate-${t.tenant_id}`}>
                        Réactiver
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => remove(t.tenant_id)} className="h-8 text-xs text-red-600 hover:text-red-700 border-red-200" data-testid={`tenant-del-${t.tenant_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



/**
 * Reusable cleanup bar shown at the top of "Réservations" or "Utilisateurs" tab.
 *
 * - kind="bookings": opens a modal where ALL items are PRE-CHECKED.
 *   Admin unchecks the ones to keep, then "Supprimer la selection" deletes the rest.
 * - kind="users":    opens a modal where ALL items are UN-CHECKED.
 *   Admin checks the ones to delete, then "Supprimer la selection" deletes them.
 *
 * In both cases:
 * - "Tout selectionner / Tout deselectionner" toggles the lot
 * - The admin himself is filtered out at the prop level (parent never includes him)
 * - The endpoint receives an explicit list of IDs
 */

/**
 * BlockedSignupsPanel — small telemetry card listing the disposable / spam
 * email domains that tried to register recently. Sorted by attempt count.
 * The admin can:
 *   • see the top N domains + total attempts + last attempt date,
 *   • copy a domain string (to add it to /app/backend/disposable_domains.py
 *     when redeploying), or
 *   • reset a single row (once added, or if the domain was a false positive).
 * Rendered at the bottom of the "Utilisateurs" tab.
 */
function BlockedSignupsPanel({ getHeaders }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/blocked-signups`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      // Non-blocking: keep the panel simple if the endpoint fails
      setData({ total_attempts: 0, domains: [] });
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  React.useEffect(() => { load(); }, [load]);

  const resetDomain = async (domain) => {
    if (!window.confirm(`Réinitialiser le compteur pour ${domain} ?`)) return;
    try {
      await axios.delete(`${API}/admin/blocked-signups/${encodeURIComponent(domain)}`, {
        headers: getHeaders(),
        withCredentials: true,
      });
      toast.success(`Compteur réinitialisé pour ${domain}`);
      load();
    } catch {
      toast.error('Réinitialisation impossible');
    }
  };

  const copy = async (s) => {
    try { await navigator.clipboard.writeText(s); toast.success(`${s} copié`); }
    catch { toast.error('Copie impossible'); }
  };

  if (loading) return null;
  if (!data || !data.domains || data.domains.length === 0) return null;

  return (
    <div
      className="mt-6 bg-white border border-[#E2E2DF] rounded-md p-4"
      data-testid="blocked-signups-panel"
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#C77D63]" />
          <h3 className="font-heading text-sm font-semibold text-[#1A3C34]">
            Tentatives d&apos;inscription bloquées (emails jetables)
          </h3>
        </div>
        <Badge className="bg-[#C77D63]/10 text-[#C77D63] text-xs" data-testid="blocked-signups-total">
          {data.total_attempts} tentative{data.total_attempts > 1 ? 's' : ''} sur {data.domains.length} domaine{data.domains.length > 1 ? 's' : ''}
        </Badge>
      </div>
      <p className="text-[11px] text-[#737373] mb-3">
        Ce compteur s&apos;incrémente à chaque inscription refusée par la blocklist.
        Si un domaine remonte souvent et semble légitime, retirez-le de{' '}
        <code className="bg-[#F0EFEA] px-1 rounded text-[10px]">disposable_domains.py</code>.
        Si un nouveau domaine spam apparaît hors blocklist, il n&apos;apparaîtra <em>pas</em>{' '}
        ici — surveillez plutôt vos vraies inscriptions.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="blocked-signups-table">
          <thead className="text-[11px] text-[#737373] border-b border-[#E2E2DF]">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">Domaine</th>
              <th className="text-right px-2 py-1.5 font-medium">Tentatives</th>
              <th className="text-left px-2 py-1.5 font-medium">Dernière</th>
              <th className="text-left px-2 py-1.5 font-medium">Exemples</th>
              <th className="text-right px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {data.domains.map((row) => (
              <tr key={row.domain} className="border-b border-[#F0EFEA]" data-testid={`blocked-row-${row.domain}`}>
                <td className="px-2 py-1.5 font-mono text-[12px] text-[#1A3C34]">
                  <button
                    onClick={() => copy(row.domain)}
                    className="hover:underline"
                    title="Copier"
                  >
                    {row.domain}
                  </button>
                </td>
                <td className="px-2 py-1.5 text-right font-medium text-[#1A3C34]">{row.count}</td>
                <td className="px-2 py-1.5 text-[11px] text-[#737373]">
                  {row.last_attempt_at ? new Date(row.last_attempt_at).toLocaleString('fr-BE') : '—'}
                </td>
                <td className="px-2 py-1.5 text-[10px] text-[#737373] max-w-xs truncate">
                  {(row.sample_emails || []).slice(-2).join(', ')}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => resetDomain(row.domain)}
                    className="h-7 px-2 text-red-500 hover:bg-red-50"
                    data-testid={`blocked-reset-${row.domain}`}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CleanupBar({ kind, items, getHeaders, reload }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [checked, setChecked] = React.useState(new Set());
  // Trash (24h restore window)
  const [trashItems, setTrashItems] = React.useState([]);
  const [trashOpen, setTrashOpen] = React.useState(false);
  const [trashChecked, setTrashChecked] = React.useState(new Set());

  // Load trash count on mount + after delete actions
  const loadTrash = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cleanup/trash/${kind}`, {
        headers: getHeaders(), withCredentials: true,
      });
      setTrashItems(res.data.items || []);
    } catch (err) {
      // silent — endpoint optional during rollout
    }
  }, [kind, getHeaders]);
  React.useEffect(() => { loadTrash(); }, [loadTrash]);

  // When opening: bookings = all checked (pre-selected for deletion);
  //               users   = all unchecked (admin picks who to remove)
  const openModal = () => {
    if (kind === 'bookings') {
      const ids = items.map(b => b.booking_id);
      setChecked(new Set(ids));
    } else {
      setChecked(new Set());
    }
    setOpen(true);
  };

  const toggle = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (checked.size === items.length) {
      setChecked(new Set());
    } else {
      setChecked(new Set(items.map(it => kind === 'bookings' ? it.booking_id : it.user_id)));
    }
  };

  const submit = async () => {
    const ids = Array.from(checked);
    if (ids.length === 0) {
      toast.error('Aucun element selectionne');
      return;
    }
    if (!window.confirm(`Confirmez la suppression DEFINITIVE de ${ids.length} ${kind === 'bookings' ? 'reservation(s)' : 'utilisateur(s)'} ?`)) return;
    setBusy(true);
    try {
      const url = `${API}/admin/cleanup/${kind}`;
      const res = await axios.post(url, { ids }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      const count = kind === 'bookings' ? res.data.deleted_bookings : res.data.deleted_users;
      toast.success(`${count} element(s) deplace(s) en corbeille (restauration possible 24h)`);
      setOpen(false);
      loadTrash();
      if (reload) reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur cleanup');
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    const ids = Array.from(trashChecked);
    if (ids.length === 0) {
      toast.error('Aucun element selectionne');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/restore/${kind}`, { ids }, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      toast.success(`${res.data.restored} element(s) restaure(s)`);
      setTrashOpen(false);
      setTrashChecked(new Set());
      loadTrash();
      if (reload) reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur restauration');
    } finally {
      setBusy(false);
    }
  };

  const toggleTrash = (id) => {
    setTrashChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Hours remaining for an item before hard-purge
  const hoursLeft = (deletedAtIso) => {
    if (!deletedAtIso) return 0;
    const deleted = new Date(deletedAtIso).getTime();
    const expires = deleted + 24 * 3600 * 1000;
    const remaining = Math.max(0, Math.round((expires - Date.now()) / 3600000));
    return remaining;
  };

  const label = kind === 'bookings' ? 'reservations' : 'utilisateurs';
  const helperText = kind === 'bookings'
    ? `Toutes pre-cochees. Decochez celles a CONSERVER, le reste sera supprime.`
    : `Cochez les utilisateurs a SUPPRIMER. (Admin toujours preserve.)`;

  return (
    <>
      <div className="flex items-center justify-between bg-red-50/40 border border-red-200 rounded-lg p-3" data-testid={`cleanup-bar-${kind}`}>
        <div className="flex items-center gap-2 text-sm text-red-700">
          <Trash2 className="w-4 h-4" />
          <span><strong>Nettoyage</strong> · {items.length} {label} en base</span>
          {trashItems.length > 0 && (
            <span className="ml-3 text-xs bg-amber-100 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">
              Corbeille : {trashItems.length} restaurable(s) (24h)
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {trashItems.length > 0 && (
            <Button
              onClick={() => { setTrashChecked(new Set()); setTrashOpen(true); }}
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50 rounded-md h-9 px-3 text-xs font-semibold"
              data-testid={`open-trash-${kind}`}
            >
              ↺ Corbeille ({trashItems.length})
            </Button>
          )}
          <Button
            onClick={openModal}
            disabled={items.length === 0}
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white rounded-md h-9 px-4 text-xs font-semibold"
            data-testid={`open-cleanup-${kind}`}
          >
            Nettoyer les {label}
          </Button>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid={`cleanup-modal-${kind}`}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-[#E2E2DF]">
              <h3 className="font-heading text-lg font-bold text-[#1A3C34] flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" /> Nettoyage des {label}
              </h3>
              <p className="text-xs text-[#737373] mt-1">{helperText}</p>
            </div>

            <div className="px-5 py-3 border-b border-[#E2E2DF] flex items-center justify-between bg-[#F9F9F7]">
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs font-semibold text-[#C77D63] hover:underline"
                data-testid={`toggle-all-${kind}`}
              >
                {checked.size === items.length ? 'Tout deselectionner' : 'Tout selectionner'}
              </button>
              <span className="text-xs text-[#737373]">{checked.size} / {items.length} selectionne(s)</span>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {items.map((it) => {
                const id = kind === 'bookings' ? it.booking_id : it.user_id;
                const isChecked = checked.has(id);
                return (
                  <label
                    key={id}
                    className={`flex items-center gap-3 p-2 rounded-md cursor-pointer ${isChecked ? 'bg-red-50 border border-red-200' : 'hover:bg-[#F9F9F7] border border-transparent'}`}
                    data-testid={`cleanup-row-${id}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(id)}
                      className="w-4 h-4 accent-red-600"
                    />
                    <div className="flex-1 min-w-0 text-xs">
                      {kind === 'bookings' ? (
                        <>
                          <div className="font-semibold text-[#1A3C34] truncate">
                            {it.user_name || it.user_email || 'Anonyme'} · {it.space_name || it.space_id}
                          </div>
                          <div className="text-[#737373]">
                            {it.booking_date} · {it.start_time}-{it.end_time} · {it.status} · {it.total_price}€
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold text-[#1A3C34] truncate">{it.name}</div>
                          <div className="text-[#737373]">
                            {it.email} · {it.booking_count || 0} reservation(s) · cree le {(it.created_at || '').slice(0, 10)}
                          </div>
                        </>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="p-4 border-t border-[#E2E2DF] flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="rounded-md h-9 text-xs"
                data-testid={`cleanup-cancel-${kind}`}
              >
                Annuler
              </Button>
              <Button
                onClick={submit}
                disabled={busy || checked.size === 0}
                className="bg-red-600 hover:bg-red-700 text-white rounded-md h-9 px-5 text-xs font-bold"
                data-testid={`cleanup-confirm-${kind}`}
              >
                {busy ? 'Suppression...' : `Supprimer ${checked.size} element(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Restore (trash) modal */}
      {trashOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid={`trash-modal-${kind}`}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-[#E2E2DF]">
              <h3 className="font-heading text-lg font-bold text-amber-700 flex items-center gap-2">
                ↺ Corbeille - {label}
              </h3>
              <p className="text-xs text-[#737373] mt-1">
                Cochez les elements a restaurer. Apres 24h depuis la suppression, ils sont definitivement effaces.
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {trashItems.map((it) => {
                const id = kind === 'bookings' ? it.booking_id : it.user_id;
                const isChecked = trashChecked.has(id);
                const hLeft = hoursLeft(it._backup_info?.deleted_at);
                return (
                  <label
                    key={id}
                    className={`flex items-center gap-3 p-2 rounded-md cursor-pointer ${isChecked ? 'bg-amber-50 border border-amber-200' : 'hover:bg-[#F9F9F7] border border-transparent'}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleTrash(id)}
                      className="w-4 h-4 accent-amber-600"
                    />
                    <div className="flex-1 min-w-0 text-xs">
                      {kind === 'bookings' ? (
                        <>
                          <div className="font-semibold text-[#1A3C34] truncate">
                            {it.user_name || it.user_email || 'Anonyme'} · {it.space_name || it.space_id}
                          </div>
                          <div className="text-[#737373]">
                            {it.booking_date} · {it.start_time}-{it.end_time} · {it.total_price}€
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold text-[#1A3C34] truncate">{it.name}</div>
                          <div className="text-[#737373]">{it.email}</div>
                        </>
                      )}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${hLeft <= 2 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {hLeft}h restantes
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="p-4 border-t border-[#E2E2DF] flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setTrashOpen(false)}
                disabled={busy}
                className="rounded-md h-9 text-xs"
              >
                Fermer
              </Button>
              <Button
                onClick={restore}
                disabled={busy || trashChecked.size === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-md h-9 px-5 text-xs font-bold"
                data-testid={`trash-restore-${kind}`}
              >
                {busy ? 'Restauration...' : `Restaurer ${trashChecked.size} element(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}



function ProvisionDeviceButton({ getHeaders, onProvisioned }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({ device_id: '', label: '', description: '' });
  const [result, setResult] = React.useState(null); // { install_command, expires_at }

  const submit = async () => {
    if (!form.device_id.trim()) {
      toast.error("device_id requis (ex: 'porte-entree')");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/door-devices/provision`, form, {
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      setResult(res.data);
      if (onProvisioned) onProvisioned();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur generation');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setResult(null);
    setForm({ device_id: '', label: '', description: '' });
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Commande copiee');
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-[#C77D63] text-white hover:bg-[#b06a51] h-8 text-xs"
        data-testid="provision-device-btn"
      >
        + Ajouter un Raspberry
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full">
            <div className="p-5 border-b border-[#E2E2DF]">
              <h3 className="font-heading text-lg font-bold text-[#1A3C34]">
                {result ? 'Commande de provisionnement' : 'Ajouter un Raspberry Pi'}
              </h3>
            </div>

            {!result ? (
              <div className="p-5 space-y-3">
                <p className="text-xs text-[#737373]">
                  Apres validation, vous obtiendrez une commande a coller sur le Pi (apres avoir lance <code>install.sh</code>).
                  Plus besoin de copier la cle API a la main.
                </p>
                <div>
                  <label className="text-xs font-semibold text-[#1A3C34]">Identifiant (device_id)</label>
                  <Input
                    value={form.device_id}
                    onChange={(e) => setForm({ ...form, device_id: e.target.value })}
                    placeholder="ex: porte-entree"
                    className="h-9 text-sm mt-1"
                    data-testid="provision-device-id"
                  />
                  <p className="text-[10px] text-[#737373] mt-0.5 italic">Lettres / chiffres / - / _ uniquement</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#1A3C34]">Nom affiche (label)</label>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="ex: Porte d'entree principale"
                    className="h-9 text-sm mt-1"
                    data-testid="provision-label"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#1A3C34]">Description (optionnel)</label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="ex: Batiment A, niveau 0"
                    className="h-9 text-sm mt-1"
                  />
                </div>
              </div>
            ) : (
              <div className="p-5 space-y-3">
                <p className="text-sm text-[#1A3C34]">
                  ✓ Appareil <strong>{result.device_id}</strong> enregistre. Commande generee, valable <strong>60 minutes</strong>.
                </p>
                <div className="bg-[#1A3C34] rounded-md p-3 font-mono text-xs text-white break-all leading-relaxed" data-testid="provision-command">
                  {result.install_command}
                </div>
                <Button
                  onClick={() => copy(result.install_command)}
                  className="w-full bg-[#C77D63] text-white hover:bg-[#b06a51] h-9 text-xs"
                  data-testid="copy-provision-command"
                >
                  📋 Copier la commande
                </Button>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 space-y-1">
                  <p className="font-semibold">Procedure sur le Raspberry Pi :</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-amber-700">
                    <li>Connectez-vous au Pi en SSH</li>
                    <li>Si <code>install.sh</code> n'a pas encore ete lance : <code>cd ~/Flexi-location/raspberry &amp;&amp; bash install.sh</code> (laissez la cle API vide)</li>
                    <li>Collez et executez la commande ci-dessus</li>
                    <li>Le Pi apparait <strong>automatiquement</strong> en ligne ici dans les 30 secondes</li>
                  </ol>
                </div>
              </div>
            )}

            <div className="p-4 border-t border-[#E2E2DF] flex justify-end gap-2">
              <Button variant="outline" onClick={close} disabled={busy} className="h-9 text-xs">
                {result ? 'Fermer' : 'Annuler'}
              </Button>
              {!result && (
                <Button onClick={submit} disabled={busy || !form.device_id.trim()} className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 h-9 text-xs px-5">
                  {busy ? 'Generation...' : 'Generer la commande'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* ============================================================
 * Brochure Leads (contacts qui ont télécharge la brochure PDF)
 * ============================================================ */
function AdminBrochureLeads({ getHeaders }) {
  const [rows, setRows] = React.useState([]);
  const [stats, setStats] = React.useState({ total_leads: 0, total_downloads: 0 });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const load = React.useCallback(async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/admin/brochure-leads`, { headers: getHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRows(data.leads || []);
      setStats({ total_leads: data.total_leads || 0, total_downloads: data.total_downloads || 0 });
    } catch (e) {
      setError('Impossible de charger les leads.');
    } finally { setBusy(false); }
  }, [API, getHeaders]);

  React.useEffect(() => { load(); }, [load]);

  const del = async (email) => {
    if (!window.confirm(`Supprimer le lead ${email} ?`)) return;
    try {
      const res = await fetch(`${API}/admin/brochure-leads/${encodeURIComponent(email)}`,
        { method: 'DELETE', headers: getHeaders() });
      if (!res.ok) throw new Error();
      await load();
    } catch { alert("Suppression échouée."); }
  };

  const exportCsv = () => {
    const headers = ['prenom', 'email', 'lang', 'download_count', 'first_download_at', 'last_download_at', 'last_ip'];
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(',')];
    for (const r of rows) lines.push(headers.map(h => escape(r[h])).join(','));
    const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `flexi-location-leads-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const mailtoAll = () => {
    const bcc = rows.map(r => r.email).filter(Boolean).join(',');
    if (!bcc) return;
    window.location.href = `mailto:?bcc=${bcc}&subject=${encodeURIComponent('Flexi-location — nouvelle offre')}`;
  };

  return (
    <div className="space-y-4" data-testid="admin-brochure-leads">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="text-xs text-[#737373] uppercase tracking-wide">Leads uniques</div>
          <div className="mt-1 font-mono text-2xl font-semibold text-[#1A3C34]">{stats.total_leads}</div>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="text-xs text-[#737373] uppercase tracking-wide">Téléchargements totaux</div>
          <div className="mt-1 font-mono text-2xl font-semibold text-[#1A3C34]">{stats.total_downloads}</div>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="text-xs text-[#737373] uppercase tracking-wide">Ratio téléchargements / lead</div>
          <div className="mt-1 font-mono text-2xl font-semibold text-[#1A3C34]">
            {stats.total_leads > 0 ? (stats.total_downloads / stats.total_leads).toFixed(2) : '—'}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={load} disabled={busy} data-testid="brochure-leads-refresh"
          className="bg-[#1A3C34] text-white hover:bg-[#1A3C34]/90 h-9 text-xs">
          {busy ? 'Chargement…' : 'Actualiser'}
        </Button>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={rows.length === 0}
          data-testid="brochure-leads-csv" className="h-9 text-xs">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
        <Button size="sm" variant="outline" onClick={mailtoAll} disabled={rows.length === 0}
          data-testid="brochure-leads-mail" className="h-9 text-xs">
          E-mail à tous (BCC)
        </Button>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="brochure-leads-table">
          <thead className="bg-[#F9F9F7] border-b border-[#E2E2DF]">
            <tr className="text-left text-xs uppercase tracking-wide text-[#737373]">
              <th className="px-4 py-3">Prénom</th>
              <th className="px-4 py-3">E-mail</th>
              <th className="px-4 py-3">Langue</th>
              <th className="px-4 py-3 text-center">DL</th>
              <th className="px-4 py-3">Premier</th>
              <th className="px-4 py-3">Dernier</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#737373]">
                Aucun lead pour l'instant. Le compteur se remplira dès qu'un visiteur téléchargera la brochure.
              </td></tr>
            ) : rows.map(r => (
              <tr key={r.email} className="border-t border-[#F0EFEA] hover:bg-[#F9F9F7]/60">
                <td className="px-4 py-2.5 font-medium text-[#1A3C34]">{r.prenom}</td>
                <td className="px-4 py-2.5 font-mono text-xs">
                  <a href={`mailto:${r.email}`} className="text-[#C77D63] hover:underline">{r.email}</a>
                </td>
                <td className="px-4 py-2.5"><Badge variant="outline" className="uppercase text-[10px]">{r.lang}</Badge></td>
                <td className="px-4 py-2.5 text-center font-mono">{r.download_count || 1}</td>
                <td className="px-4 py-2.5 text-xs text-[#737373]">{(r.first_download_at || '').replace('T', ' ').slice(0, 16)}</td>
                <td className="px-4 py-2.5 text-xs text-[#737373]">{(r.last_download_at || '').replace('T', ' ').slice(0, 16)}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="sm" variant="ghost" onClick={() => del(r.email)}
                    data-testid={`brochure-lead-delete-${r.email}`}
                    className="h-7 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50">
                    Supprimer
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

